import { OSF } from "../lib/osfmos";
import { Constants } from "./constants";
import { ConversionContext } from "./conversionContext";
import { IconBuilder } from "./iconBuilder";
import { LocaleBuilder } from "./localeBuilder";
import * as MOS from "./mosManifest";
import { MosManifest } from "./mosManifest";
import { Utilities } from "./utilities";

import * as util from "util";
const { Utils } = require("./osfUtils");

Utils.OverrideMethods();

export enum RuntimeSource {
  DefaultSettingSourceLocations = "DefaultSettingsSourceLocations",
  ExecuteFunction = "ExecuteFunction",
  LaunchEvent = "LaunchEvent",
  SharedRuntimes = "SharedRuntimes",
  ShowTaskpane = "ShowTaskpane",
  ReportPhishing = "ReportPhishing"
}

// Should be synced with the RuntimeLifeTime enum in shared_src\runtime\Utilities\WAC\OSFManifest.ts
export enum InputRuntimeLifeTime {
  Short = 0,
  Long,
}

export enum ResourceType {
  AllLocaleImages = "AllLocaleImages",
  AllLocaleUrls = "AllLocaleUrls",
  AllLocaleLongStrings = "AllLocaleLongStrings",
  AllLocaleShortStrings = "AllLocaleShortStrings",
}

export enum CommandSurfaceType {
  None = "None",
  Ribbon = "Ribbon",
  ContextMenu = "ContextMenu",
}

// The legacy manifest contains Actions in different places.  The actions operate within the context of a webpage.  In
// the new meta os manifest, these different urls are broken up into different "runtimes".  In order to implement this
// translation, we make two passes over the manifest:  One pass to collect all the different possible runtimes.  Such an
// occurence is stored as a "RuntimeRecords".  These records are later consolidated into a set of runtimes in the new manifest.
export class RuntimeRecord {
  _resource: string;
  _localeResources: {};
  _lifetime: MOS.RuntimesLifetime = MOS.RuntimesLifetime.Short;
  _isResId: boolean;
  _isCapabilitiesSorted: boolean = false;
  _requirements: MOS.RequirementsExtensionElement;
  _source: RuntimeSource;
  _manifestHostType: OSF.ManifestHostType; // workbook, presentation, document etc.
  _versionOverrideCapabilities: MOS.Capabilities[];
  _versionOverridesNumber: OSF.VersionOverridesNumber; // the version override that this runtime record came from.  If it came from the main <OfficeApp> then it is null.
  _scriptResId: string = null;
  _inputFormFactor: string = null;

  constructor() {}

  sortCapabilities() {
    if (!this._isCapabilitiesSorted && !Utilities.isNullOrUndefined(this._versionOverrideCapabilities)) {
      Converter._sortCapabilitiesAlphabatically(this._versionOverrideCapabilities);
      this._isCapabilitiesSorted = true;
    }
  }

  getKey() {
    return Converter._generateRuntimeLookupKey(this._resource,
      this._lifetime,
      this._versionOverrideCapabilities,
      this._inputFormFactor,
      this._source);
  }
}

export class Converter {
  private _addedExecuteFunctions: Object;
  private _mosManifest: MosManifest;
  private _xmlManifest: OSF.Manifest.OfficeAppManifest;
  private _localeBuilder: LocaleBuilder;
  private _iconBuilder: IconBuilder;
  private _runtimeLookup: Object;
  private _uniqueSuffix: number;
  private _uniqueRuntimeSuffix: number;
  private _uniqueActionSuffix: number;
  private _uniqueViewSuffix: number;

  constructor(inputXml: string, locale: string) {
    this._mosManifest = new MosManifest();
    this._xmlManifest = new OSF.Manifest.OfficeAppManifest(inputXml, locale, Utilities.getVerbosity());
    this._localeBuilder = new LocaleBuilder();
    this._iconBuilder = new IconBuilder();
    this._uniqueSuffix = 0;
    this._uniqueRuntimeSuffix = 0;
    this._uniqueActionSuffix = 0;
    this._uniqueViewSuffix = 0;
    this._runtimeLookup = {};
    this._addedExecuteFunctions = {};
  }

  getMosManifest(): MosManifest {
    return this._mosManifest;
  }

  getLocaleBuilder(): LocaleBuilder {
    return this._localeBuilder;
  }

  convert(): void {
    let context: ConversionContext = new ConversionContext();
    this._populateRoot(context);
  }

  async downloadAndStoreIcons(folderPath: string) {
    // Now download all the icons and store them in the package directory
    await this._iconBuilder.downloadAndStoreIcons(folderPath);
  }

  private getNextUniqueSuffix(): number {
    return ++this._uniqueSuffix;
  }

  private getNextRuntimeSuffix(): number {
    return ++this._uniqueRuntimeSuffix;
  }

  private getNextActionSuffix(): number {
    return ++this._uniqueActionSuffix;
  }

  private getNextViewSuffix(): number {
    return ++this._uniqueViewSuffix;
  }

  private _populateRoot(context: ConversionContext): void {
    // Parts relevant to the legacy Office manifest

    // Use this line to reference devpreview schema
    // this._mosManifest.$schema = Constants.MosManifestSchemaVersionDevPreview;

    // Use this line to reference devpreview schema
    // this._mosManifest.manifestVersion = MOSMosManifestManifestVersion.DevPreview;

    // Use this line to reference GA schema
    this._mosManifest.$schema = Constants.MosManifestSchemaVersionGA;

    // If the schema name has been overridden on the command line then
    // we override it with this value.
    if (!Utilities.isNullOrUndefined(Utilities._schemaOverride)) {
      this._mosManifest.$schema = Utilities._schemaOverride;
    }

    // Use this line to reference the GA schema version that is currently being used
    this._mosManifest.manifestVersion = Constants.MosManifestGAVersion_1_17;

    // If the schema version name has been overridden on the command line then
    // we override it with this value.
    if (!Utilities.isNullOrUndefined(Utilities._schemaVersionOverride)) {
      this._mosManifest.manifestVersion = Utilities._schemaVersionOverride;
    }

    this._mosManifest.id = this._xmlManifest.getMarketplaceID();
    this._mosManifest.version = Converter._fixVersionFormat(this._xmlManifest.getMarketplaceVersion());

    this._populateRootName();
    this._populateRootDescription();
    this._populateRootIcons();
    this._mosManifest.accentColor = Constants.DefaultAccessColor; // there is no accent color in the legacy manifest
    this._populateDeveloper();
    this._populateLocalizationInfo();
    this._populateRootPermissions();
    this._populateRootDevicePermissions();
    this._populateAuthorization();
    this._populateWebApplicationInfos();
    this._populateExtension(context);
    this._populateRootRequirements();
    this._populateValidDomains();

    // Not present in legacy Office manifest
    this._populateStaticTabs();
    this._populateComposeExtensions();
    this._populateGraphConnector();
    this._populateConfigurableTabs();
    this._populateBots();
    this._populateConnectors();
    this._populateActivities();
    this._populateMeetingExtensionDefinition();
    this._populateSubscriptionOffer();
    this._populateShowLoadingIndicator();
    this._populateIsFullScreen();
    this._populateConfigurableProperties();
    this._populateDefaultBlockUntilAdminAction();
    this._populatePublisherDocsUrl();
    this._populateDefaultInstallScope();
    this._populateDefaultGroupCapability();
  }

  private _populateRootName(): void {
    let name: MOS.Name = new MOS.Name();

    // Populate the default language display name in the body of the manifest
    name.short = Utilities.getLimitedString(
      this._xmlManifest.getDefaultDisplayName(),
      Constants.MaxResourceStringLength30,
      "name.short truncated to %d characters",
    );

    name.full = Utilities.getLimitedString(
      this._xmlManifest.getDefaultDisplayName(),
      Constants.MaxResourceStringLength100,
      "name.full truncated to %d characters",
    ); // no long name in legacy manifest

    // Populate resources for other languages of display names
    // The "seen locales" are all the different locales that were seen during parsing of the xml manifest but there is not necessarily
    // a resource for every language for every property.
    this._populateLocaleSensitiveProperty(this._xmlManifest.getDisplayName, Constants.MosNameShort, null);
    this._populateLocaleSensitiveProperty(this._xmlManifest.getDisplayName, Constants.MosNameFull, null);

    this._mosManifest.name = name;
  }

  private _populateRootDescription(): void {
    let description: MOS.Description = new MOS.Description();

    let legacyDescription: string = this._xmlManifest.getDefaultDescription();

    if (!Utilities.isNullOrUndefined(legacyDescription)) {
      description.short = Utilities.getLimitedString(
        legacyDescription,
        Constants.MaxResourceStringLength80,
        "description.short truncated to %d characters",
      );

      description.full = legacyDescription; // no full description in legacy manifest
    }

    this._populateLocaleSensitiveProperty(
      this._xmlManifest.getDescription,
      Constants.MosDescriptionShort,
      Constants.MaxResourceStringLength80,
    );
    this._populateLocaleSensitiveProperty(
      this._xmlManifest.getDescription,
      Constants.MosDescriptionFull,
      Constants.MaxResourceStringLength4000,
    );
    this._mosManifest.description = description;
  }

  private _populateRootIcons(): void {
    let icons: MOS.Icons = new MOS.Icons();

    const defaultLocale: string = this._xmlManifest.getDefaultLocale();

    let defaultIconUrl: string = this._xmlManifest.getIconUrl(defaultLocale);
    if (Utilities.isNullOrUndefined(defaultIconUrl)) {
      defaultIconUrl = this._xmlManifest.getDefaultIconUrl();
    }

    if (Utilities.isNullOrUndefined(defaultIconUrl)) {
      defaultIconUrl = Constants.IconsOutline + ".png";
    }

    icons.outline = this._iconBuilder.addIconAndGetReference(
      defaultIconUrl,
      true /* getShortNameAndStore */,
      true /* forRootIcon */,
    );

    let defaultHighResolutionIconUrl: string = this._xmlManifest.getHighResolutionIconUrl(defaultLocale);

    if (Utilities.isNullOrUndefined(defaultHighResolutionIconUrl)) {
      defaultHighResolutionIconUrl = this._xmlManifest.getDefaultHighResolutionIconUrl();
    }

    if (Utilities.isNullOrUndefined(defaultHighResolutionIconUrl)) {
      defaultHighResolutionIconUrl = Constants.IconsColor + ".png";
    }

    icons.color = this._iconBuilder.addIconAndGetReference(
      defaultHighResolutionIconUrl,
      true /* getShortNameAndStore */,
      true /* forRootIcon */,
    );

    this._mosManifest.icons = icons;
  }

  private _populateDeveloper(): void {
    let developer: MOS.Developer = new MOS.Developer();

    developer.name = Utilities.getLimitedString(
      this._xmlManifest.getProviderName(),
      Constants.MaxResourceStringLength32,
      "developer.name truncated to %d characters",
    );

    developer.mpnId = undefined; // Not present in legacy manifest
    let supportUrl: string =
      this._xmlManifest.getSupportUrl(this._xmlManifest.getDefaultLocale()) || Constants.DefaultDeveloperUrl;

    // The legacy xml manifest only has <SupportUrl> while the metaos manifest has these three urls in the developer section.
    // We currently use the support url for all three since there is no exact mapping.
    Utilities.log(
      "Using <SupportUrl> from legacy manifest to populate developer.privacyUrl, developer.termsOfUseUrl and developer.websiteUrl in metaos manifest.",
    );
    developer.privacyUrl = supportUrl;
    developer.termsOfUseUrl = supportUrl;
    developer.websiteUrl = supportUrl;

    this._mosManifest.developer = developer;
  }

  private _populateLocalizationInfo(): void {
    let localizationInfo: MOS.LocalizationInfo = new MOS.LocalizationInfo();
    localizationInfo.defaultLanguageTag = LocaleBuilder.fixLocaleCasing(this._xmlManifest.getDefaultLocale());

    for (let seenLocale in this._xmlManifest.getLocalesSeen()) {
      if (seenLocale != localizationInfo.defaultLanguageTag) {
        let additionalLanguages: MOS.AdditionalLanguages = new MOS.AdditionalLanguages();
        additionalLanguages.languageTag = seenLocale;
        additionalLanguages.file = Utilities.createLocaleFileName(seenLocale);

        if (Utilities.isNullOrUndefined(localizationInfo.additionalLanguages)) {
          localizationInfo.additionalLanguages = [];
        }

        localizationInfo.additionalLanguages.push(additionalLanguages);
      }
    }

    this._mosManifest.localizationInfo = localizationInfo;
  }

  private _populateWebApplicationInfos(): void {
    this._populateWebApplicationInfoFromContentAndTaskpane();
    this._populateWebApplicationInfoFromMailbox();
  }

  // Handles getting the webapplicationinfo from the MailHost manifests
  private _populateWebApplicationInfoFromMailbox(): void {
    let overrideIndexes: string[] = this._getMailHostOverrides();

    let mosWebApplicationInfo: MOS.WebApplicationInfo = new MOS.WebApplicationInfo();

    for (let overrideIndex of overrideIndexes) {
      let webApplicationInfo: Object =
        this._xmlManifest._versionOverrides[Constants.InputMailHost]?.[overrideIndex]?._webApplicationInfo;

      // If there is no information in the webApplicationInfo then there is nothing to convert
      if (
        Utilities.isNullOrUndefined(webApplicationInfo) ||
        (Utilities.isNullOrUndefined(webApplicationInfo["_webApplicationId"]) &&
          Utilities.isNullOrUndefined(webApplicationInfo["_webApplicationResource"]))
      ) {
        continue;
      }

      mosWebApplicationInfo.id = webApplicationInfo["_webApplicationId"];
      mosWebApplicationInfo.resource = webApplicationInfo["_webApplicationResource"];

      if (Utilities.isNullOrUndefined(this._mosManifest.webApplicationInfo)) {
        this._mosManifest.webApplicationInfo = mosWebApplicationInfo;
      } else {
        // If this._mosManifest.webApplicationInfo has been set before then it could have been set by another
        // MailHost version override or it could have been set by a TaskPane app processed before these.
        // Currently the metaos schema only supports one single webapplicationinfo but the legacy
        // schema can have different ones for different override versions.
        // TODO: https://office.visualstudio.com/OC/_workitems/edit/6224818/
        Utilities.logError(
          "MetaOS schema currently only supports one webapplicationInfo but we have found more than one in this manifest MailHost override: " +
            overrideIndex +
            ". " +
            " app: " +
            this._xmlManifest.getDisplayName(this._xmlManifest.getDefaultLocale()) +
            " WI:6224818",
        );
        return;
      }
    }
  }

  // handles getting the webapplicationinfo from Content and Taskpane manifests
  private _populateWebApplicationInfoFromContentAndTaskpane(): void {
    // Currently the metaos schema only supports one single webapplicationinfo but the legacy
    // schema can have different ones for different override versions.
    // TODO: https://office.visualstudio.com/OC/_workitems/edit/6224818/
    let mosWebApplicationInfo: MOS.WebApplicationInfo = new MOS.WebApplicationInfo();
    let shouldBeAdded: boolean = false;

    if (!Utilities.isNullOrUndefined(this._xmlManifest.getWebApplicationId())) {
      mosWebApplicationInfo.id = this._xmlManifest.getWebApplicationId();
      shouldBeAdded = true;
    }

    if (!Utilities.isNullOrUndefined(this._xmlManifest.getWebApplicationResource())) {
      mosWebApplicationInfo.resource = this._xmlManifest.getWebApplicationResource();
      shouldBeAdded = true;
    }

    if (shouldBeAdded) {
      this._mosManifest.webApplicationInfo = mosWebApplicationInfo;
    }
  }

  private _populateRootPermissions(): void {
    // TODO: Not present in legacy manifest
  }

  private _populateRootDevicePermissions(): void {
    // TODO: Not present in legacy manifest
  }

  private _populateAuthorization(): void {
    // Currently the metaos schema only supports one single authorization but the legacy
    // schema can have different ones for different override versions.
    // TODO: https://office.visualstudio.com/OC/_workitems/edit/6224818/
    let mosAuthorization: MOS.Authorization = new MOS.Authorization();

    let permissions: any = this._xmlManifest.getPermission();

    let mosPermissions: MOS.Permissions2 = null;

    if (!Utilities.isNullOrUndefined(permissions)) {
      mosPermissions = new MOS.Permissions2();
      mosPermissions.resourceSpecific = [];
      let mosResourceSpecific: MOS.ResourceSpecific = new MOS.ResourceSpecific();
      mosResourceSpecific.name = Converter._mosPermissionfromXmlPermission(permissions, this._xmlManifest._target);
      mosResourceSpecific.type = MOS.ResourceSpecificType.Delegated;
      mosPermissions.resourceSpecific.push(mosResourceSpecific);
      mosAuthorization.permissions = mosPermissions;
    }

    // Now also handle the <ExtendedPermissions> if present
    // These only apply to VersionOverride 1.1 for Mail/Outlook Apps
    let extendedPermissions: string[] = this._xmlManifest.getExtendedPermissions(OSF.VersionOverridesNumber.V1_1);

    if (!Utilities.isNullOrUndefined(extendedPermissions) && extendedPermissions.length > 0) {
      for (let perm of extendedPermissions) {
        if (perm === Constants.InputAppendOnSend) {
          if (Utilities.isNullOrUndefined(mosPermissions)) {
            mosPermissions = new MOS.Permissions2();
            mosPermissions.resourceSpecific = [];
          }

          // Now create the permission for MailboxItem.AppendOnSend.User in the metaos manifest and add it
          let mosResourceSpecific: MOS.ResourceSpecific = new MOS.ResourceSpecific();
          mosResourceSpecific.name = Constants.MailboxItemAppendOnSendUser;
          mosResourceSpecific.type = MOS.ResourceSpecificType.Delegated;
          mosPermissions.resourceSpecific.push(mosResourceSpecific);
          mosAuthorization.permissions = mosPermissions;
        } else {
          Utilities.logWarning("Ignoring unsupported ExtendedPermission: %s", perm);
        }
      }
    }

    // Now look for SupportsSharedFolders
    // SupportsSharedFolders is only supported in VersionOverride 1.1
    let supportsSharedFolders: boolean = this._xmlManifest.getSupportsSharedFolders(
      OSF.VersionOverridesNumber.V1_1,
      Constants.InputDesktopFormFactor,
    );

    if (supportsSharedFolders) {
      if (Utilities.isNullOrUndefined(mosPermissions)) {
        mosPermissions = new MOS.Permissions2();
        mosPermissions.resourceSpecific = [];
      }

      // Now create the permission for Mailbox.AccessSharedFolders.User in the metaos manifest and add it
      let mosResourceSpecific: MOS.ResourceSpecific = new MOS.ResourceSpecific();
      mosResourceSpecific.name = Constants.MailboxAccessSharedFoldersUser;
      mosResourceSpecific.type = MOS.ResourceSpecificType.Delegated;
      mosPermissions.resourceSpecific.push(mosResourceSpecific);
      mosAuthorization.permissions = mosPermissions;
    }

    if (!Utilities.isNullOrUndefined(mosPermissions)) {
      this._mosManifest.authorization = mosAuthorization;
    }
  }

  private _populateExtension(context: ConversionContext): void {
    let extension: MOS.Extensions = new MOS.Extensions();

    this._populateAlternates(extension);
    this._populateContextMenus(extension);
    this._populateGetStartedMessages(context, extension);
    this._populateKeyboards(extension);
    this._populateExtensionRequirements(extension);
    this._populateRuntimes(context, extension);
    this._populateRibbonsAndContextMenus(context, extension);
    this._populateAutoRunEvents(context, extension);
    this._populateFormSettings(extension);

    if (Utilities.isNullOrUndefined(this._mosManifest.extensions)) {
      this._mosManifest.extensions = [];
    }

    this._addRuntimeActionLocalizedDisplayNames(extension.runtimes);
    this._mosManifest.extensions.push(extension);
  }

  private _populateAlternates(extension: MOS.Extensions): void {
    let alternates: MOS.Alternates[] = [];

    let defaultLocale: string = this._xmlManifest.getDefaultLocale();
    let iconUrl: string = this._xmlManifest.getDefaultIconUrl();
    let highResolutionIconUrl: string = this._xmlManifest.getHighResolutionIconUrl(defaultLocale);

    // Now ensure that the alternate icons section added
    if (!Utilities.isNullOrUndefined(iconUrl) || !Utilities.isNullOrUndefined(highResolutionIconUrl)) {
      let alternate: MOS.Alternates = new MOS.Alternates();
      alternate.alternateIcons = new MOS.AlternateIcons();

      if (!Utilities.isNullOrUndefined(iconUrl)) {
        alternate.alternateIcons.icon.url = iconUrl;
        let iconSize: number = Constants.LegacyWXPAppIconSize;

        if (this._xmlManifest._hostType && this._xmlManifest.getTarget() == OSF.OfficeAppType.MailApp) {
          iconSize = Constants.LegacyMailAppIconSize;
        }

        alternate.alternateIcons.icon.size = iconSize;

        for (let seenLocale in this._xmlManifest.getLocalesSeen()) {
          // skip the defaule since it is set in the main body of the manifest
          if (seenLocale !== defaultLocale) {
            const locIconUrl: string = this._xmlManifest.getIconUrl(seenLocale);
            let formattedValue: string = Converter._formatResourceStringWithVariableNumberParameters(
              Constants.AlternatesIconUrl,
              alternates.length,
            );
            this._localeBuilder.addLocaleString(seenLocale, formattedValue, locIconUrl);
          }
        }
      }

      if (!Utilities.isNullOrUndefined(highResolutionIconUrl)) {
        alternate.alternateIcons.highResolutionIcon.url = highResolutionIconUrl;
        let highResolutionIconSize: number = Constants.LegacyWXPAppHighResolutionIconSize;

        // 8856256 - This needs to be fixed after the 128 size is included or alternate icons structure is changed
        //if (this._xmlManifest._hostType && this._xmlManifest.getTarget() == OSF.OfficeAppType.MailApp) {
        //    highResolutionIconSize = Constants.LegacyMailAppHighResolutionIconSize;
        //}

        alternate.alternateIcons.highResolutionIcon.size = highResolutionIconSize;

        for (let seenLocale in this._xmlManifest.getLocalesSeen()) {
          // skip the defaule since it is set in the main body of the manifest
          if (seenLocale !== defaultLocale) {
            const locHighResolutionIconUrl: string = this._xmlManifest.getHighResolutionIconUrl(seenLocale);
            let formattedValue: string = Converter._formatResourceStringWithVariableNumberParameters(
              Constants.AlternatesHighResolutionIconUrl,
              alternates.length,
            );
            this._localeBuilder.addLocaleString(seenLocale, formattedValue, locHighResolutionIconUrl);
          }
        }
      }

      alternates.push(alternate);
    }

    // Now look for equivalent addins and add them as alternates if present
    let equivalentAddins: OSF.Manifest.EquivalentAddin[] = this._xmlManifest.getEquivalentAddins();
    if (!Utilities.isNullOrUndefined(equivalentAddins) && equivalentAddins.length > 0) {
      for (const equivalentAddin of equivalentAddins) {
        let alternate: MOS.Alternates = new MOS.Alternates();
        alternate.prefer = new MOS.Prefer();
        if (equivalentAddin.isComAddin()) {
          alternate.prefer.comAddin = new MOS.ComAddin();
          alternate.prefer.comAddin.progId = equivalentAddin.getProgId() as string;
        } else if (equivalentAddin.isXLLCompatible()) {
          alternate.prefer.xllCustomFunctions = new MOS.XllCustomFunctions();
          alternate.prefer.xllCustomFunctions.fileName = equivalentAddin.getFileName() as string;
        } else {
          Utilities.logWarning("Unknown equivalent add-in type encountered.");
          continue; // skip equivalent addin of unknown type
        }

        alternates.push(alternate);
      }
    }

    if (alternates.length > 0) {
      extension.alternates = alternates;
    }
  }

  private _populateAutoRunEvents(context: ConversionContext, extension: MOS.Extensions): void {
    // Only outlook has autoruns
    if (Utilities.isNullOrUndefined(this._xmlManifest._versionOverrides[Constants.InputMailHost])) {
      return;
    }

    // If neither of the two outlook overrides are present then there are no launchevents/autoruns to process
    let overrides: string[] = this._getMailHostOverrides();

    // If there are no outlook overrides then there are no autoruns
    if (overrides.length == 0) {
      return;
    }

    let autoRunEvents: MOS.AutoRunEvents[] = [];

    for (let overrideId of overrides) {
      let addinCommandsExtensionPointsDesktop: Object[] = Converter._getAddinCommandsExtensionPoints(
        Constants.InputMailHost,
        overrideId,
        Constants.InputDesktopFormFactor,
        this._xmlManifest,
      );

      let addinCommandsExtensionPointsMobile: Object[] = Converter._getAddinCommandsExtensionPoints(
        Constants.InputMailHost,
        overrideId,
        Constants.InputMobileFormFactor,
        this._xmlManifest,
      );

      Converter._setVersionOverrideNumberAndCapabilitiesInContext(
        context,
        this._xmlManifest,
        Constants.InputMailHost,
        overrideId,
      );

      // If this override has a desktop form factor then look for an autorun extension point in it
      if (!Utilities.isNullOrUndefined(addinCommandsExtensionPointsDesktop)) {
        context.setFormFactor(Constants.InputDesktopFormFactor);
        this._populateAutoRunEventsForFormFactor(context, addinCommandsExtensionPointsDesktop, Constants.InputDesktopFormFactor, autoRunEvents);
        // We are done processing the desktop form factor so we unset it in the context
        context.setFormFactor(null);
      }

      // If this override has a mobile form factor then look for an autorun extension point in it
      if (!Utilities.isNullOrUndefined(addinCommandsExtensionPointsMobile)) {
        context.setFormFactor(Constants.InputMobileFormFactor);
        this._populateAutoRunEventsForFormFactor(context, addinCommandsExtensionPointsMobile, Constants.InputMobileFormFactor, autoRunEvents);
        // We are done processing the mobile form factor so we unset it in the context
        context.setFormFactor(null);
      }

      // Clear this in the context since we are done creating the autorun events
      Converter._clearVersionOverrideNumberAndCapabilitiesInContext(context);
    }

    if (autoRunEvents.length > 0) {
      extension.autoRunEvents = autoRunEvents;
    }
  }

  private _populateAutoRunEventsForFormFactor(context: ConversionContext, addinCommandsExtensionPoints: Object[], formFactor: string, autoRunEvents: MOS.AutoRunEvents[]): void {
    
    for (let extensionPoint of addinCommandsExtensionPoints) {
      if (
        extensionPoint[Constants.InputType] === Constants.InputLaunchEvent &&
        !Utilities.isNullOrUndefined(extensionPoint[Constants.InputEvents])
      ) {
        let autoRunEvent: MOS.AutoRunEvents = null;

        // Now loop over each autorun event
        for (let innerEvent of extensionPoint[Constants.InputEvents]) {
          if (Utilities.isNullOrUndefined(autoRunEvent)) {
            autoRunEvent = new MOS.AutoRunEvents();
            autoRunEvent.events = [];
            autoRunEvent.requirements = new MOS.RequirementsExtensionElement();
            autoRunEvent.requirements.capabilities = [];
            autoRunEvent.requirements.scopes = [];
            autoRunEvent.requirements.scopes.push(MOS.Scopes.Mail);

            // Merge in any capabilities that come from the requirement sets at the version override scope of the legacy manifest
            Converter._mergeInVersionOverrideRequirements(
              context.getCurrentVersionOverrideCapabilities(),
              autoRunEvent.requirements,
            );

            // If the form factor is set in the context then we set it on the autoRunEvent
            if (!Utilities.isNullOrUndefined(context.getFormFactor())) {
              autoRunEvent.requirements.formFactors = [];
              autoRunEvent.requirements.formFactors.push(Converter._inputFormFactorToMOSFormFactor(context.getFormFactor()));
            }

            autoRunEvents.push(autoRunEvent);
          }

          let mosEvent: MOS.Events = new MOS.Events();

          // Now add the corresponding action to a runtime
          let runtime: MOS.Runtimes = null;

          const runtimeUrl: string = extensionPoint[Constants.InputResid];
          const runtimeKey: string = Converter._generateRuntimeLookupKey(
            runtimeUrl,
            MOS.RuntimesLifetime.Short,
            context.getCurrentVersionOverrideCapabilities(),
            context.getFormFactor()
          );

          if (!Utilities.isNullOrUndefined(runtimeKey)) {
            runtime = this._runtimeLookup[runtimeKey];
          }

          if (!Utilities.isNullOrUndefined(runtime)) {
            // For LaunchEvents, the function name should be the action id.
            let actionId: string = innerEvent[Constants.InputFunctionName];

            // Even if we don't find a function name, we try to to generate a unique action
            // name like "action_3"
            if (Utilities.isNullOrUndefined(actionId)) {
              actionId = innerEvent[Constants.InputAction];
              actionId = this._createUniqueActionId(actionId);
              Utilities.logError("Unable to get functionName from LaunchEvent.  Using %s instead.", actionId);
            }

            if (Utilities.isNullOrUndefined(runtime.actions)) {
              runtime.actions = [];
            }

            let action: MOS.ExtensionRuntimesActionsItem = new MOS.ExtensionRuntimesActionsItem();
            action.id = actionId;
            action.displayName = actionId;
            mosEvent.actionId = actionId;
            action.type = MOS.ExtensionRuntimesActionsItemType.ExecuteFunction;

            this._addRuntimeExecuteAction(runtime, action, runtimeKey);
          } else {
            Utilities.assert(!Utilities.isNullOrUndefined(runtime), "Expected runtime to be cached.");
            Utilities.logError("Did not find runtime %s for autorunevent.", runtimeKey);
          }

          // Now add the event to the autoruns
          const sendMode: string = innerEvent[Constants.InputSendMode];

          if (!Utilities.isNullOrUndefined(sendMode) && sendMode.length > 0) {
            mosEvent.options = new MOS.Options();
            mosEvent.options.sendMode = Converter._mosSendModeFromXmlSendMode(sendMode);
          }

          mosEvent.type = Converter._legacyLaunchEventToMosEventType(innerEvent[Constants.InputType]);
          autoRunEvent.events.push(mosEvent);
        }
      }
    }
  }

  private _populateContextMenus(extension: MOS.Extensions): void {}

  private _populateGetStartedMessages(context: ConversionContext, extension: MOS.Extensions): void {
    // Get started messages apply to WXP and TaskPaneApp
    if (!Utilities.isNullOrUndefined(this._xmlManifest._versionOverrides[Constants.InputWorkbook])) {
      this._fillGetStartedMessagesFromVersionOverrides(context, extension, OSF.ManifestHostType.Workbook);
    } else if (!Utilities.isNullOrUndefined(this._xmlManifest._versionOverrides[Constants.InputDocument])) {
      this._fillGetStartedMessagesFromVersionOverrides(context, extension, OSF.ManifestHostType.Document);
    } else if (!Utilities.isNullOrUndefined(this._xmlManifest._versionOverrides[Constants.InputPresentation])) {
      this._fillGetStartedMessagesFromVersionOverrides(context, extension, OSF.ManifestHostType.Presentation);
    }
  }

  private _fillGetStartedMessagesFromVersionOverrides(
    context: ConversionContext,
    extension: MOS.Extensions,
    manifestHostType: OSF.ManifestHostType,
  ): void {
    var hostType: string = Converter._getVersionOverrideHostTypeFromOsfManifestHostType(manifestHostType);
    var versionOverride: object = this._xmlManifest._versionOverrides[hostType];

    if (Utilities.isNullOrUndefined(versionOverride)) {
      return;
    }

    var getStartedNode: object = versionOverride[Constants.InputGetStartedNode];

    if (!Utilities.isNullOrUndefined(getStartedNode)) {
      Converter._setVersionOverrideNumberAndCapabilitiesInContext(context, this._xmlManifest, hostType, null);
      let getStartedMessage: MOS.GetStartedMessages = new MOS.GetStartedMessages();
      getStartedMessage.title = getStartedNode[Constants.InputTitle];
      getStartedMessage.description = getStartedNode[Constants.InputDescription];
      getStartedMessage.learnMoreUrl = getStartedNode[Constants.InputLearnMoreUrl];

      // Set the requirements based on the context of what we have in the versionOverride
      getStartedMessage.requirements = new MOS.RequirementsExtensionElement();
      getStartedMessage.requirements.capabilities = Converter._cloneCapabilities(
        context.getCurrentVersionOverrideCapabilities(),
      );

      let scope: MOS.Scopes = Converter._getRequirementsScopeFromManifestHostType(manifestHostType);
      getStartedMessage.requirements.scopes = [];
      getStartedMessage.requirements.scopes.push(scope);

      // Get started messages are only supported inside of DesktopFormFactor
      // https://learn.microsoft.com/en-us/javascript/api/manifest/getstarted?view=common-js-preview
      getStartedMessage.requirements.formFactors = [];
      getStartedMessage.requirements.formFactors.push(MOS.FormFactors.Desktop);

      extension.getStartedMessages = [];
      extension.getStartedMessages.push(getStartedMessage);
      Converter._clearVersionOverrideNumberAndCapabilitiesInContext(context);
    }

    return;
  }

  private _populateKeyboards(extension: MOS.Extensions): void {
    this._xmlManifest.getExtendedOverrides();
  }

  private _populateFormSettings(extension: MOS.Extensions): void {
    // Only outlook has formSettings
    if (Utilities.isNullOrUndefined(this._xmlManifest._versionOverrides[Constants.InputMailHost])) {
      return;
    }

    const formSettings: OSF.Manifest.FormSetting[][] = this._xmlManifest.getFormSettings();
    const defaultLocale: string = this._xmlManifest.getDefaultLocale();
    // Make sure to get the "first" DesktopSettings either for ItemRead or ItemEdit
    for (let item in formSettings) {
      const formSetting: OSF.Manifest.FormSetting = formSettings[item][Constants.InputDesktopSettings];
      const sourceLocation: string = formSetting?.SourceLocation?.[defaultLocale];
      if (!Utilities.isNullOrUndefined(sourceLocation)) {
        extension.audienceClaimUrl = sourceLocation;

        // Now set the corresponding localized strings
        const defaultLocale: string = this._xmlManifest.getDefaultLocale().toLocaleLowerCase();

        for (let seenLocale in this._xmlManifest.getLocalesSeen()) {
          // skip the defaule since it is set in the main body of the manifest
          if (seenLocale !== defaultLocale) {
            const locFormSetting: string = formSetting?.SourceLocation?.[seenLocale];
            this._localeBuilder.addLocaleString(seenLocale, Constants.ExtensionAudienceClaimUrl, locFormSetting);
          }
        }
        break;
      }
    }
  }

  private _populateExtensionRequirements(extension: MOS.Extensions): void {
    let requirements: MOS.RequirementsExtensionElement = new MOS.RequirementsExtensionElement();

    // TODO: Needs review.  Currently no mapping for <Requirements><Method> from legacy manifest
    if (!Utilities.isNullOrUndefined(this._xmlManifest.getRequirements()?.sets)) {
      requirements.capabilities = Converter._getCapabilitiesFromLegacyRequirementSets(
        this._xmlManifest.getRequirements().sets,
      );
    }

    this._addScopesFromHosts(requirements);

    extension.requirements = requirements;
  }

  private static _getCapabilitiesFromLegacyRequirementSets(sets: any[]): MOS.Capabilities[] {
    let capabilities: MOS.Capabilities[] = null;

    for (let i: number = 0; i < sets.length; i++) {
      let requirementSet = sets[i];
      let capability: MOS.Capabilities = new MOS.Capabilities();

      capability.name = requirementSet.name;
      capability.minVersion = requirementSet.version;

      if (Utilities.isNullOrUndefined(capabilities)) {
        capabilities = [] as MOS.Capabilities[];
      }

      capabilities!.push(capability);
    }

    return capabilities;
  }

  private _populateRibbonsAndContextMenus(context: ConversionContext, extension: MOS.Extensions): void {
    let ribbons: MOS.Ribbons[] = [];
    let contextMenus: MOS.ContextMenus[] = [];

    // Unlike the MOS manifest, in the legacy manifest, each host type must have its own ribbon
    // Therefore, we create one ribbon for each host type if one exists in the legacy manifest
    this._addRibbonOrContextMenuForHostType(context, OSF.ManifestHostType.Mailbox, ribbons, contextMenus);
    this._addRibbonOrContextMenuForHostType(context, OSF.ManifestHostType.Workbook, ribbons, contextMenus);
    this._addRibbonOrContextMenuForHostType(context, OSF.ManifestHostType.Document, ribbons, contextMenus);
    this._addRibbonOrContextMenuForHostType(context, OSF.ManifestHostType.Presentation, ribbons, contextMenus);

    if (ribbons.length > 0) {
      extension.ribbons = ribbons;
    }

    if (contextMenus.length > 0) {
      extension.contextMenus = contextMenus;
    }

    // Unset the ribbon and context menu indexes since we know that we are done with processing
    // all context menus and ribbons.
    context.setCurrentRibbonIndex(ConversionContext.UnsetIndex);
    context.setCurrentContextMenuIndex(ConversionContext.UnsetIndex);
  }

  private _addRuntimeActionLocalizedDisplayNames(runtimes: MOS.Runtimes[]): void {
    if (Utilities.isNullOrUndefined(runtimes)) {
      return;
    }

    for (var runtimeIdx = 0; runtimeIdx < runtimes.length; runtimeIdx++) {
      var runtime: MOS.Runtimes = runtimes[runtimeIdx];

      if (!Utilities.isNullOrUndefined(runtime) && !Utilities.isNullOrUndefined(runtime.actions)) {
        for (var actionIdx = 0; actionIdx < runtime.actions.length; actionIdx++) {
          var action: MOS.ExtensionRuntimesActionsItem = runtime.actions[actionIdx];
          this._addConstantResourceForAllSeenLocales(
            action.displayName,
            Constants.RuntimesActionsDisplayName,
            runtimeIdx,
            actionIdx,
            null,
            null,
            null,
            null,
          );
        }
      }
    }
  }

  private _addRibbonOrContextMenuForHostType(
    context: ConversionContext,
    manifestHostType: OSF.ManifestHostType,
    ribbons: MOS.Ribbons[],
    contextMenus: MOS.ContextMenus[],
  ): void {
    // If this host does not have any content in the manifest then we can skip the logic below
    if (!this._doesHostHaveContentInManifest(manifestHostType)) {
      return;
    }

    context.setManifestHostType(manifestHostType);
    let hostType: string = Converter._getVersionOverrideHostTypeFromOsfManifestHostType(manifestHostType);

    if (Converter._isWXP(hostType)) {
      Converter._setVersionOverrideNumberAndCapabilitiesInContext(
        context,
        this._xmlManifest,
        hostType,
        OSF.VersionOverridesNumber.V1_0.toString(),
      );
      let functionFileResid: Object = Converter._getFunctionFileResid(hostType, null, this._xmlManifest);
      if (
        !Utilities.isNullOrUndefined(functionFileResid) &&
        !Utilities.isNullOrUndefined(functionFileResid[Constants.InputDesktopFormFactor])
      ) {
        context.setCurrentFunctionFileResid(functionFileResid[Constants.InputDesktopFormFactor]);
      }

      // First look for information under the main manifest
      // Using DesktopFormFactor here because it is the only place that supports ribbon command surfaces
      // MobileFormFactor uses: MobileMessageReadCommandSurface, MobileOnlineMetingCommandSurface
      let addinCommandsExtensionPoints: [] = Converter._getAddinCommandsExtensionPoints(
        hostType,
        null,
        Constants.InputDesktopFormFactor,
        this._xmlManifest,
      );

      if (!Utilities.isNullOrUndefined(addinCommandsExtensionPoints)) {
        this._addRibbonOrContextMenuForManifestSection(
          context,
          hostType,
          null,
          addinCommandsExtensionPoints,
          ribbons,
          contextMenus,
        );
      }

      context.setCurrentFunctionFileResid(null);

      // Clear the override version requirements from the context
      Converter._clearVersionOverrideNumberAndCapabilitiesInContext(context);
    } else if (hostType === Constants.InputMailHost) {
      // Now look for extension points in each override
      let supportedVersionOverrides = Converter._getMailHostVersionOVerrides();
      for (let vo of supportedVersionOverrides) {
        // Now look for extension points under each of the overrides

        // Get the DesktopFormFactor command surfaces / extension points
        let addinCommandsExtensionPoints: [] = Converter._getAddinCommandsExtensionPoints(
          hostType,
          vo,
          Constants.InputDesktopFormFactor,
          this._xmlManifest,
        );

        Converter._setVersionOverrideNumberAndCapabilitiesInContext(context, this._xmlManifest, hostType, vo);

        if (!Utilities.isNullOrUndefined(addinCommandsExtensionPoints)) {
          let functionFileResidVersion: Object = Converter._getFunctionFileResid(hostType, vo, this._xmlManifest);
          if (
            !Utilities.isNullOrUndefined(functionFileResidVersion) &&
            !Utilities.isNullOrUndefined(functionFileResidVersion[Constants.InputDesktopFormFactor])
          ) {
            context.setCurrentFunctionFileResid(functionFileResidVersion[Constants.InputDesktopFormFactor]);
          }

          this._addRibbonOrContextMenuForManifestSection(
            context,
            hostType,
            vo,
            addinCommandsExtensionPoints,
            ribbons,
            contextMenus,
          );
          context.setCurrentFunctionFileResid(null);
        }

        // Get the MobileFormFactor extension points
        // MobileFormFactor uses: MobileMessageReadCommandSurface, MobileOnlineMetingCommandSurface, MobileOnlineMeetingCommandSurface
        // MobileLogEventAppointmentAttendee, MobileAppointmentOrganizerCommandSurface,
        // Currently not implemented: MobileMessageComposeCommandSurface, MobileAppointmentAttendeeCommandSurface
        let addinCommandsExtensionPointsMobile: [] = Converter._getAddinCommandsExtensionPoints(
          hostType,
          vo,
          Constants.InputMobileFormFactor,
          this._xmlManifest,
        );

        if (
          !Utilities.isNullOrUndefined(addinCommandsExtensionPointsMobile) &&
          addinCommandsExtensionPointsMobile.length > 0
        ) {
          let functionFileResidVersion: Object = Converter._getFunctionFileResid(hostType, vo, this._xmlManifest);
          if (
            !Utilities.isNullOrUndefined(functionFileResidVersion) &&
            !Utilities.isNullOrUndefined(functionFileResidVersion[Constants.InputMobileFormFactor])
          ) {
            context.setCurrentFunctionFileResid(functionFileResidVersion[Constants.InputMobileFormFactor]);
          }

          this._addRibbonOrContextMenuForManifestSection(
            context,
            hostType,
            vo,
            addinCommandsExtensionPointsMobile,
            ribbons,
            contextMenus,
          );
          context.setCurrentFunctionFileResid(null);
        }

        // Clear the override version requirements from the context
        Converter._clearVersionOverrideNumberAndCapabilitiesInContext(context);
      }
    } else {
      Utilities.logError("Unsupported host skipping processing for host type: %s", hostType);
    }

    context.setManifestHostType(null);
  }

  private _addRibbonOrContextMenuForManifestSection(
    context: ConversionContext,
    versionOverrideHostIndex: string,
    versionOverrideIndex: string,
    addinCommandsExtensionPoints: [],
    ribbons: MOS.Ribbons[],
    contextMenus: MOS.ContextMenus[],
  ) {
    let len: number = addinCommandsExtensionPoints.length;

    for (let idx: number = 0; idx < len; idx++) {
      let extPoint: Object = addinCommandsExtensionPoints[idx];
      let extensionPointType: string = extPoint[Constants.InputType];

      // Only look for the ribbon since we are populating the ribbon now
      if (Converter._isRibbonCommandSurface(extensionPointType)) {
        context.setFormFactor(Constants.InputDesktopFormFactor);
        context.setCurrentCommandSurfaceType(CommandSurfaceType.Ribbon);
        let ribbon: MOS.Ribbons = new MOS.Ribbons();

        // Keep track of a count of the ribbon index for purposes of the localization indexed strings
        Converter._incrementCurrentRibbonIndex(context);

        ribbon.tabs = [];

        // Now populate all the tabs
        if (!Utilities.isNullOrUndefined(extPoint[Constants.InputTabs])) {
          for (let tabIdx: number = 0; tabIdx < extPoint[Constants.InputTabs].length; tabIdx++) {
            const tab: any = extPoint[Constants.InputTabs][tabIdx];
            context.setCurrentTabIndex(tabIdx);
            const mosTab: any = this._populateTab(context, tab);
            context.setCurrentTabIndex(ConversionContext.UnsetIndex);
            ribbon.tabs.push(mosTab);
          }
        }

        ribbon.contexts = [];
        ribbon.contexts.push(Converter._getRibbonContextFromCommandSurface(extPoint[Constants.InputType]));

        ribbon.requirements = new MOS.RequirementsExtensionElement();

        this._populateRibbonAndContextMenuRequirements(context, ribbon.requirements);

        Utilities.log("Adding ribbon for: %s and vo: %s", versionOverrideHostIndex, versionOverrideIndex);
        ribbons.push(ribbon);
        context.setCurrentCommandSurfaceType(CommandSurfaceType.None);
        context.setFormFactor(null);
      } else if (Converter._isContextMenuCommandSurface(extensionPointType)) {
        context.setFormFactor(Constants.InputDesktopFormFactor);
        context.setCurrentCommandSurfaceType(CommandSurfaceType.ContextMenu);
        let contextMenu: MOS.ContextMenus = new MOS.ContextMenus();

        // Keep track of a count of the context menu index for purposes of the localization indexed strings
        if (context.getCurrentContextMenuIndex() === ConversionContext.UnsetIndex) {
          context.setCurrentContextMenuIndex(0);
        } else {
          context.setCurrentContextMenuIndex(context.getCurrentContextMenuIndex() + 1);
        }

        // Now populate all the menu
        if (!Utilities.isNullOrUndefined(extPoint[Constants.InputChildren])) {
          // Map OfficeMenuNode => extension.contextMenus[x].menus[x]
          for (let menuNodeIdx: number = 0; menuNodeIdx < extPoint[Constants.InputChildren].length; menuNodeIdx++) {
            const menuNode: any = extPoint[Constants.InputChildren][menuNodeIdx];
            const mosMenu: MOS.Menus = new MOS.Menus();

            mosMenu.entryPoint = menuNode[Constants.InputType] === Constants.InputContextMenuCell
              ? MOS.MenusEntryPoint.Cell
              : MOS.MenusEntryPoint.Text;

            context.setCurrentContextMenuMenuIndex(menuNodeIdx);

            // Map "menuControl" => extension.contextMenus[x].menus[x].controls[x]
            // These controls are either Menus or Buttons
            for (
              let menuControlIdx: number = 0;
              menuControlIdx < menuNode[Constants.InputChildren].length;
              menuControlIdx++
            ) {
              const control: any = menuNode[Constants.InputChildren][menuControlIdx];

              context.setCurrentContextMenuMenuControlIndex(menuControlIdx);

              if (Utilities.isNullOrUndefined(mosMenu.controls)) {
                mosMenu.controls = [];
              }

              // The type of context menu controls is either Menu or Button and we default to Button
              if (control[Constants.InputControlType] === Constants.InputMenu) {

                let mosMenuMenu: MOS.ExtensionCommonCustomControlMenu = new MOS.ExtensionCommonCustomControlMenu();
                this._populateMenuControl(context, mosMenuMenu, control);
                mosMenu.controls.push(<MOS.ExtensionCommonCustomGroupControlsItem>(<unknown>mosMenuMenu));
              } else {
                let mosButton: MOS.ExtensionCommonCustomGroupControlsItem = new MOS.ExtensionCommonCustomGroupControlsItem();
                this._populateButtonControl(context, mosButton, control);
                mosMenu.controls.push(mosButton);
              }
            }

            if (Utilities.isNullOrUndefined(contextMenu.menus)) {
              contextMenu.menus = [];
            }

            contextMenu.menus.push(mosMenu);

            // Unset the menuControl index
            context.setCurrentContextMenuMenuControlIndex(ConversionContext.UnsetIndex);
          }

          // Unset the menu index
          context.setCurrentContextMenuMenuIndex(ConversionContext.UnsetIndex);
        }

        contextMenu.requirements = new MOS.RequirementsExtensionElement();

        this._populateRibbonAndContextMenuRequirements(context, contextMenu.requirements);

        Utilities.log("Adding contextMenu for: %s and vo: %s", versionOverrideHostIndex, versionOverrideIndex);

        contextMenus.push(contextMenu);
        context.setCurrentCommandSurfaceType(CommandSurfaceType.None);
        context.setFormFactor(null);
      } else if (Converter._isMobileCommandSurface(extensionPointType)) {
        context.setFormFactor(Constants.InputMobileFormFactor);
        context.setCurrentCommandSurfaceType(CommandSurfaceType.Ribbon);
        let ribbon: MOS.Ribbons = new MOS.Ribbons();
        ribbon.tabs = [];
        ribbon.tabs[0] = new MOS.ExtensionRibbonsArrayTabsItem();
        ribbon.tabs[0].customMobileRibbonGroups = [];

        // 8856256 - current ribbon schema requires groups[] even though it should not be needed
        // fix this once schema is updated.
        ribbon.tabs[0].groups = [];
        let placeholderGroup: MOS.ExtensionRibbonsCustomTabGroupsItem = new MOS.ExtensionRibbonsCustomTabGroupsItem();
        placeholderGroup.label = "placeholder";
        placeholderGroup.controls = [];
        let placeholderControl: MOS.ExtensionCommonCustomGroupControlsItem =
          new MOS.ExtensionCommonCustomGroupControlsItem();
        placeholderControl.actionId = "";
        placeholderControl.label = "";
        placeholderControl.id = "";
        placeholderControl.type = MOS.ExtensionCommonCustomGroupControlsItemType.Menu;
        placeholderControl.icons = [];
        let placeholderIcon: MOS.ExtensionCommonIcon = new MOS.ExtensionCommonIcon();
        placeholderIcon.url = "https://placeholder";
        placeholderIcon.size = 32;
        placeholderControl.icons.push(placeholderIcon);
        placeholderControl.supertip = new MOS.ExtensionCommonSuperToolTip();
        placeholderControl.supertip.description = "";
        placeholderControl.supertip.title = "";
        placeholderGroup.controls.push(placeholderControl);
        ribbon.tabs[0].groups.push(placeholderGroup);

        let customMobileGroup: MOS.ExtensionRibbonsCustomMobileGroupItem =
          new MOS.ExtensionRibbonsCustomMobileGroupItem();

        // Set to a default value in case there isn't actually a group id that gets set below
        customMobileGroup.id = this._createUniqueId("customMobileGroup");
        customMobileGroup.label = customMobileGroup.id;
        ribbon.tabs[0].customMobileRibbonGroups[0] = customMobileGroup;
        ribbon.tabs[0].id = this._createUniqueId("mobileTab");
        ribbon.tabs[0].label = ribbon.tabs[0].id;
        ribbon.contexts = [];
        ribbon.contexts.push(Converter._getRibbonContextFromCommandSurface(extPoint[Constants.InputType]));

        ribbon.requirements = new MOS.RequirementsExtensionElement();

        this._populateRibbonAndContextMenuRequirements(context, ribbon.requirements);

        var mobileControl: Object = null;

        switch (extensionPointType) {
          case Constants.InputMobileMessageReadCommandSurface:
          case Constants.InputMobileOnlineMeetingCommandSurface:
          case Constants.InputMobileLogEventAppointmentAttendee:
            // Keep track of a count of the ribbon index for purposes of the localization indexed strings
            Converter._incrementCurrentRibbonIndex(context);

            // Currently these mobile groups and controls in xml only support one tab, one group and one control
            context.setCurrentTabIndex(0);
            context.setCurrentCustomMobileGroupIndex(0);

            // InputMobileMessageReadCommandSurface has a <Group> wrapping the <Control> but
            // InputMobileOnlineMeetingCommandSurface and InputMobileLogEventAppointmentAttendee only
            // have <Control> directly inside of the extension point node.
            // Handle the <Group> if there is one.
            if (!Utilities.isNullOrUndefined(extPoint[Constants.InputGroup])) {
              if (!Utilities.isNullOrUndefined(extPoint[Constants.InputGroup][Constants.InputId])) {
                customMobileGroup.id = extPoint[Constants.InputGroup][Constants.InputId];
              }

              if (
                !Utilities.isNullOrUndefined(extPoint[Constants.InputGroup][Constants.InputLabel]) ||
                !Utilities.isNullOrUndefined(extPoint[Constants.InputGroup][Constants.InputLabelResId])
              ) {
                customMobileGroup.label = this._populateControlLocalizedField(
                  context,
                  extPoint[Constants.InputGroup][Constants.InputLabelResId],
                  extPoint[Constants.InputGroup][Constants.InputLabel],
                  Constants.MaxResourceStringLength32,
                  ResourceType.AllLocaleShortStrings,
                  Constants.RibbonsTabsCustomMobileRibbonGroupsLabel,
                );
              }

              if (!Utilities.isNullOrUndefined(extPoint[Constants.InputGroup][Constants.InputControl])) {
                mobileControl = extPoint[Constants.InputGroup][Constants.InputControl];
              }
            } else {
              if (!Utilities.isNullOrUndefined(extPoint[Constants.InputControl])) {
                mobileControl = extPoint[Constants.InputControl];
              }
            }

            if (!Utilities.isNullOrUndefined(mobileControl)) {
              // Currently these mobile groups and controls in xml only support one group and one control
              context.setCurrentControlIndex(0);

              // Populate the mobile control
              customMobileGroup.controls = [];
              customMobileGroup.controls[0] = new MOS.ExtensionRibbonsCustomMobileControlButtonItem();
              this._populateMobileControlProperties(context, customMobileGroup.controls[0], mobileControl);

              context.setCurrentControlIndex(ConversionContext.UnsetIndex);
            }

            context.setCurrentCustomMobileGroupIndex(ConversionContext.UnsetIndex);
            context.setCurrentTabIndex(ConversionContext.UnsetIndex);

            break;

          // These other three seem to have nearly no documentation so we ignore these for now
          case Constants.InputMobileAppointmentAttendeeCommandSurface:
          case Constants.InputMobileAppointmentAttendeeCommandSurface:
          case Constants.InputMobileAppointmentAttendeeCommandSurface:
            break;
          default:
            Utilities.logError("Unrecognized mobile command surface: " + extensionPointType);
        }

        ribbon.tabs[0].customMobileRibbonGroups[0] = customMobileGroup;
        Utilities.log("Adding ribbon for: %s and vo: %s", versionOverrideHostIndex, versionOverrideIndex);
        ribbons.push(ribbon);
        context.setCurrentCommandSurfaceType(CommandSurfaceType.None);
        context.setFormFactor(null);
      } else if (Converter._isReportPhishingCommandSurface(extensionPointType)) {

        let fixedControl: MOS.ExtensionRibbonsArrayFixedControlItem = null;
        let spamPreProcessingDialog: MOS.ExtensionRibbonsSpamPreProcessingDialog = null;

        context.setCurrentCommandSurfaceType(CommandSurfaceType.Ribbon);
        // Keep track of a count of the ribbon index for purposes of the localization indexed strings
        Converter._incrementCurrentRibbonIndex(context);

        // Handle the fixed control
        context.setInFixedControl(true);
        context.setFormFactor(Constants.InputDesktopFormFactor);

        context.setCurrentFixedControlIndex(0);
        fixedControl = new MOS.ExtensionRibbonsArrayFixedControlItem();

        if (!Utilities.isNullOrUndefined(extPoint[Constants.InputFixedControl])) {
          this._populateButtonControl(context, <MOS.ExtensionCommonCustomGroupControlsItem>(<any>(fixedControl)), extPoint[Constants.InputFixedControl]);
        }
        context.setCurrentFixedControlIndex(0);
        context.setInFixedControl(false);

        // Handle the preProcessingDialog
        spamPreProcessingDialog = new MOS.ExtensionRibbonsSpamPreProcessingDialog();

        let spamPreProcessingDialogObj: any = extPoint[Constants.InputSpamPreProcessingDialog];

        // Handle extensions[0].ribbons[0].spamPreProcessingDialog.title
        spamPreProcessingDialog.title = this._getResourceAndWriteLocalizedValues(
          spamPreProcessingDialogObj[Constants.InputTitleResId],
          null,
          Constants.MaxResourceStringLength128,
          context.getCurrentVersionOverrideNumber(),
          Constants.RibbonsSpamProcessingDialogTitle,
          ResourceType.AllLocaleShortStrings,
          context.getCurrentRibbonIndex(),
          ConversionContext.UnsetIndex,
          ConversionContext.UnsetIndex,
          ConversionContext.UnsetIndex,
          ConversionContext.UnsetIndex
        );

        // Handle extensions[0].ribbons[0].spamPreProcessingDialog.description
        spamPreProcessingDialog.description = this._getResourceAndWriteLocalizedValues(
          spamPreProcessingDialogObj[Constants.InputDescriptionResId],
          null,
          Constants.MaxResourceStringLength250,
          context.getCurrentVersionOverrideNumber(),
          Constants.RibbonsSpamProcessingDialogDescription,
          ResourceType.AllLocaleLongStrings,
          context.getCurrentRibbonIndex(),
          ConversionContext.UnsetIndex,
          ConversionContext.UnsetIndex,
          ConversionContext.UnsetIndex,
          ConversionContext.UnsetIndex
        );

        // Handle extensions[0].ribbons[0].spamPreProcessingDialog.spamFreeTextSectionTitle
        spamPreProcessingDialog.spamFreeTextSectionTitle = this._getResourceAndWriteLocalizedValues(
          spamPreProcessingDialogObj[Constants.InputSpamFreeTextSectionLabelResId],
          null,
          Constants.MaxResourceStringLength128,
          context.getCurrentVersionOverrideNumber(),
          Constants.RibbonsSpamProcessingDialogSpamFreeTextSectionLabel,
          ResourceType.AllLocaleShortStrings,
          context.getCurrentRibbonIndex(),
          ConversionContext.UnsetIndex,
          ConversionContext.UnsetIndex,
          ConversionContext.UnsetIndex,
          ConversionContext.UnsetIndex
        );

        // Handle extensions[0].ribbons[0].spamPreProcessingDialog.spamMoreInfo
        if (!Utilities.isNullOrUndefined(extPoint[Constants.InputSpamPreProcessingDialog])) {
          spamPreProcessingDialog.spamMoreInfo = new MOS.SpamMoreInfo();
          let spamPreProcessingDialogObj: any = extPoint[Constants.InputSpamPreProcessingDialog];
          let moreInfoObj: any = spamPreProcessingDialogObj[Constants.InputSpamMoreInfo];

          if (!Utilities.isNullOrUndefined(moreInfoObj)) {
            // Handle extensions[0].ribbons[0].spamPreProcessingDialog.spamMoreInfo.text
            if (!Utilities.isNullOrUndefined(moreInfoObj[Constants.InputTextResId])) {
              spamPreProcessingDialog.spamMoreInfo.text = this._getResourceAndWriteLocalizedValues(
                moreInfoObj[Constants.InputTextResId],
                null,
                Constants.MaxResourceStringLength128,
                context.getCurrentVersionOverrideNumber(),
                Constants.RibbonsSpamProcessingDialogSpamMoreInfoText,
                ResourceType.AllLocaleShortStrings,
                context.getCurrentRibbonIndex(),
                ConversionContext.UnsetIndex,
                ConversionContext.UnsetIndex,
                ConversionContext.UnsetIndex,
                ConversionContext.UnsetIndex
              );
            }

            // Handle extensions[0].ribbons[0].spamPreProcessingDialog.spamMoreInfo.url
            if (!Utilities.isNullOrUndefined(moreInfoObj[Constants.InputUrlResId])) {
              spamPreProcessingDialog.spamMoreInfo.url = this._getResourceAndWriteLocalizedValues(
                moreInfoObj[Constants.InputUrlResId],
                null,
                Constants.MaxResourceStringLength2048,
                context.getCurrentVersionOverrideNumber(),
                Constants.RibbonsSpamProcessingDialogSpamMoreInfoUrl,
                ResourceType.AllLocaleUrls,
                context.getCurrentRibbonIndex(),
                ConversionContext.UnsetIndex,
                ConversionContext.UnsetIndex,
                ConversionContext.UnsetIndex,
                ConversionContext.UnsetIndex
              );
            }
          }
        }

        // Handle extensions[0].ribbons[0].spamPreProcessingDialog.spamReportingOptions
        if (!Utilities.isNullOrUndefined(spamPreProcessingDialogObj[Constants.InputSpamReportingOptions])) {

          spamPreProcessingDialog.spamReportingOptions = new MOS.SpamReportingOptions();
          let spamReportingOptionsObj: any = spamPreProcessingDialogObj[Constants.InputSpamReportingOptions];

          // Handle extensions[0].ribbons[0].spamPreProcessingDialog.spamReportingOptions.title
          if (!Utilities.isNullOrUndefined(spamReportingOptionsObj[Constants.InputTitleResId])) {
            spamPreProcessingDialog.spamReportingOptions.title = this._getResourceAndWriteLocalizedValues(
              spamReportingOptionsObj[Constants.InputTitleResId],
              null,
              Constants.MaxResourceStringLength128,
              context.getCurrentVersionOverrideNumber(),
              Constants.RibbonsSpamProcessingDialogSpamReportingOptionsTitle,
              ResourceType.AllLocaleShortStrings,
              context.getCurrentRibbonIndex(),
              ConversionContext.UnsetIndex,
              ConversionContext.UnsetIndex,
              ConversionContext.UnsetIndex,
              ConversionContext.UnsetIndex
            );
          }
          // Handle extensions[0].ribbons[0].spamPreProcessingDialog.spamReportingOptions.options
          if (!Utilities.isNullOrUndefined(spamReportingOptionsObj[Constants.InputOptions] &&

            spamReportingOptionsObj[Constants.InputOptions].length > 0)) {
            spamPreProcessingDialog.spamReportingOptions.options = [];

            for (let idxOptions = 0; idxOptions < spamReportingOptionsObj[Constants.InputOptions].length; idxOptions++) {
              let optResId: any = spamReportingOptionsObj[Constants.InputOptions][idxOptions];

              var optValue = this._getResourceAndWriteLocalizedValues(
                optResId,
                null,
                Constants.MaxResourceStringLength128,
                context.getCurrentVersionOverrideNumber(),
                Constants.RibbonsSpamProcessingDialogSpamReportingOptionsOptions,
                ResourceType.AllLocaleShortStrings,
                context.getCurrentRibbonIndex(),
                idxOptions,
                ConversionContext.UnsetIndex,
                ConversionContext.UnsetIndex,
                ConversionContext.UnsetIndex
              );

              spamPreProcessingDialog.spamReportingOptions.options.push(optValue);
            }
          }
        }
        
        let ribbon: MOS.Ribbons = new MOS.Ribbons();

        ribbon.contexts = [];
        ribbon.contexts.push(MOS.Contexts.SpamReportingOverride);
        ribbon.requirements = new MOS.RequirementsExtensionElement();
        this._populateRibbonAndContextMenuRequirements(context, ribbon.requirements);


        if (ribbons.length > 0) {
          // If there is a fixed control then we add it to the first ribbon
          if (!Utilities.isNullOrUndefined(fixedControl)) {
            ribbon.fixedControls = [];
            ribbon.fixedControls.push(fixedControl);
          }

          // If there is a spamPreProcessingDialog then we add it to the first ribbon
          if (!Utilities.isNullOrUndefined(spamPreProcessingDialog)) {
            ribbon.spamPreProcessingDialog = spamPreProcessingDialog;
          }
        }

        context.setFormFactor(null);

        ribbons.push(ribbon);
      }

      context.setCurrentCommandSurfaceType(CommandSurfaceType.None);
    }
  }

  private _populateRibbonAndContextMenuRequirements(
    context: ConversionContext,
    requirements: MOS.RequirementsExtensionElement,
  ): void {
    this._addRequirementSubelementScopesFromHost(context, requirements);

    // If there is a formFactor set in the context then we include it in the requirements
    if (!Utilities.isNullOrUndefined(context.getFormFactor())) {
      let formFactor: MOS.FormFactors = Converter._inputFormFactorToMOSFormFactor(context.getFormFactor());
      requirements.formFactors.push(formFactor);
    }
  }

  private _addRequirementSubelementScopesFromHost(
    context: ConversionContext,
    requirements: MOS.RequirementsExtensionElement,
  ): void {
    if (Utilities.isNullOrUndefined(requirements.scopes)) {
      requirements.scopes = [];
    }

    let scope: MOS.Scopes = Converter._getRequirementsScopeFromManifestHostType(context.getManifestHostType());

    if (!Utilities.isNullOrUndefined(scope)) {
      requirements.scopes.push(scope);
    }

    if (Utilities.isNullOrUndefined(requirements.formFactors)) {
      requirements.formFactors = [];
    }

    Converter._mergeInVersionOverrideRequirements(context.getCurrentVersionOverrideCapabilities(), requirements);
  }

  private static _mergeInVersionOverrideRequirements(
    versionOverrideCapabilities: MOS.Capabilities[],
    requirements: MOS.RequirementsExtensionElement,
  ): void {
    if (Utilities.isNullOrUndefined(versionOverrideCapabilities)) {
      return;
    }

    // We add the capabilities from the version override if they haven't already been added
    for (let capability of versionOverrideCapabilities) {
      let matchingCapability: MOS.Capabilities = null;

      if (Utilities.isNullOrUndefined(requirements.capabilities)) {
        requirements.capabilities = [];
      } else {
        matchingCapability = Converter._getMatchingCapability(requirements.capabilities, capability);
      }

      if (Utilities.isNullOrUndefined(matchingCapability)) {
        // If the capability is like "Mailbox" is not already in the collection then we simply add it
        requirements.capabilities.push(capability);
      } else {
        // If the capability is already in the collection then we want to merge in the current capability
        // min/max versions with the existing ones.

        // Convert string versions (ie "1.3") to numbers
        let incomingMin: number = Utilities.isNullOrUndefined(capability.minVersion) ? null : +capability.minVersion;
        let existingMin: number = Utilities.isNullOrUndefined(matchingCapability.minVersion)
          ? null
          : +matchingCapability.minVersion;

        // We should update the minVersion to the maximum minVersion between existingMin and, incomingMin as the maximum of the minVersion is what the Add-in supports.
        // Like between "mailbox": "1.2" and "mailbox": "1.5" we should keep "mailbox" "1.5" as it is minimum version which is supported
        if (!Utilities.isNullOrUndefined(existingMin) && !Number.isNaN(existingMin) && !Number.isNaN(incomingMin)) {
          if (incomingMin > existingMin) {
            matchingCapability.minVersion = incomingMin.toString();
          }
        }
      }
    }
  }

  private static _getMatchingCapability(
    capabilities: MOS.Capabilities[],
    capability: MOS.Capabilities,
  ): MOS.Capabilities {
    for (let cap of capabilities) {
      if (cap.name === capability.name) {
        return cap;
      }
    }

    return null;
  }

  private static _capabilitiesContainCapability(
    capabilities: MOS.Capabilities[],
    capability: MOS.Capabilities,
  ): boolean {
    for (let cap of capabilities) {
      if (
        cap.name === capability.name &&
        cap.maxVersion === capability.maxVersion &&
        cap.minVersion === capability.minVersion
      ) {
        return true;
      }
    }

    return false;
  }

  private _populateTab(context: ConversionContext, tab: Object): any {
    let mosTab: any;
    let mosGroups: MOS.ExtensionCommonCustomGroup[] = [];

    if (Utilities.isNullOrUndefined(tab[Constants.InputLabel])) {
      mosTab = new MOS.ExtensionRibbonsArrayTabsItem();
      mosTab.builtInTabId = tab[Constants.InputId];
    } else {
      mosTab = new MOS.ExtensionRibbonsArrayTabsItem();
      mosTab.id = tab[Constants.InputId];
      mosTab.label = tab[Constants.InputLabel];
    }

    this._populateGroups(context, mosGroups, tab[Constants.InputChildren]);
    mosTab.groups = mosGroups;

    return mosTab;
  }

  private _populateGroups(context: ConversionContext, mosGroups: MOS.ExtensionCommonCustomGroup[], groups: []): void {
    for (let idx: number = 0; idx < groups.length; idx++) {
      let group = groups[idx];
      let mosGroup: MOS.ExtensionCommonCustomGroup = new MOS.ExtensionCommonCustomGroup();

      context.setCurrentGroupIndex(idx);
      this._populateGroup(context, mosGroup, group);
      context.setCurrentGroupIndex(-1);
      mosGroups.push(mosGroup);
    }
  }

  private _populateGroup(context: ConversionContext, mosGroup: MOS.ExtensionCommonCustomGroup, group: Object): void {
    mosGroup.id = group[Constants.InputId];
    mosGroup.label = this._getResourceAndWriteLocalizedValues(
      group[Constants.InputLabelResId],
      group[Constants.InputLabel],
      Constants.MaxResourceStringLength64,
      context.getCurrentVersionOverrideNumber(),
      Constants.RibbonsTabsGroupsLabel,
      ResourceType.AllLocaleShortStrings,
      context.getCurrentRibbonIndex(),
      context.getCurrentTabIndex(),
      context.getCurrentGroupIndex(),
    );

    // TODO:  This property is currently not parsed by WAC legacy parser
    /* Devpreview does not have overridenbyribbonapi
        if (!Utilities.isNullOrUndefined(group[Constants.InputOverridenByRibbonApi])) {
            mosGroup.overriddenByRibbonApi = group[Constants.InputOverridenByRibbonApi];
        }
*/

    // Populate Group icons
    this._populateGroupIcons(context, mosGroup, group[Constants.InputIcon], group[Constants.InputIconResId]);

    // Populate Group controls
    mosGroup.controls = [];
    this._populateGroupControls(context, mosGroup.controls, group[Constants.InputChildren]);
  }

  private _populateGroupIcons(
    context: ConversionContext,
    group: MOS.ExtensionCommonCustomGroup,
    groupIcons: [],
    groupIconResIds: [],
  ): void {
    if (Utilities.isNullOrUndefined(groupIcons)) {
      return;
    }

    let iconIdx: number = 0;

    for (let size in groupIcons) {
      const url: string = groupIcons[size];
      const mosIcon: MOS.ExtensionCommonIcon = new MOS.ExtensionCommonIcon();

      context.setCurrentIconIndex(iconIdx);

      let fileShortName: string = this._iconBuilder.addIconAndGetReference(url, !Utilities._writeImageUrls, false);

      // formerly there was an else{} with mosIcon.file for the case when the icons are in the package
      mosIcon.url = fileShortName;

      mosIcon.size = +size;

      this._populateLocaleSensitiveProperty;

      this._getResourceAndWriteLocalizedValues(
        groupIconResIds[size],
        fileShortName,
        Constants.MaxResourceStringLength2048,
        context.getCurrentVersionOverrideNumber(),
        Utilities._writeImageUrls ? Constants.RibbonsTabsGroupsIconsUrl : Constants.RibbonsTabsGroupsIconsFile,
        ResourceType.AllLocaleImages,
        context.getCurrentRibbonIndex(),
        context.getCurrentTabIndex(),
        context.getCurrentGroupIndex(),
        context.getCurrentIconIndex(),
        ConversionContext.UnsetIndex,
      );

      if (Utilities.isNullOrUndefined(group.icons)) {
        group.icons = [];
      }

      group.icons.push(mosIcon);

      iconIdx++;
    }
  }

  private _createUniqueId(legacyId: string): string {
    let uniqueSuffix: number = this.getNextUniqueSuffix();
    return legacyId + "_" + uniqueSuffix;
  }

  private _createUniqueActionId(id: string): string {
    let uniqueSuffix: number = this.getNextActionSuffix();
    return id + "_" + uniqueSuffix;
  }

  private _createUniqueViewId(id: string): string {
    let uniqueSuffix: number = this.getNextViewSuffix();
    return id + "_" + uniqueSuffix;
  }

  private _populateGroupControls(context: ConversionContext, mosControls: any[], controls: []): void {
    for (let idx: number = 0; idx < controls.length; idx++) {
      let control: Object = controls[idx];
      context.setCurrentControlIndex(idx);

      switch (control[Constants.InputControlType]) {
        case Constants.InputButton:
          let buttonControl: MOS.ExtensionCommonCustomGroupControlsItem =
            new MOS.ExtensionCommonCustomGroupControlsItem();
          this._populateButtonControl(context, buttonControl, control);
          mosControls.push(buttonControl);
          break;
        case Constants.InputMenu:
          let menuControl: MOS.ExtensionCommonCustomControlMenu = new MOS.ExtensionCommonCustomControlMenu();
          this._populateMenuControl(context, menuControl, control);
          mosControls.push(menuControl);
          break;
        default:
          throw "need to handle legacy control type: " + control[Constants.InputControlType];
      }
    }

    context.setCurrentControlIndex(ConversionContext.UnsetIndex);
  }

  private _populateMenuControl(
    context: ConversionContext,
    menuControl: MOS.ExtensionCommonCustomControlMenu,
    control: Object,
  ): void {
    if (
      Utilities.isNullOrUndefined(control?.[Constants.InputChildren]) ||
      control[Constants.InputChildren].length == 0
    ) {
      return;
    }

    this._populateControlProperties(context, menuControl, control);

    // populate each menu item
    for (let idx: number = 0; idx < control[Constants.InputChildren].length; idx++) {
      let menuItemObject: Object = control[Constants.InputChildren][idx];
      let menuItem: MOS.ExtensionCommonCustomControlMenuItem = new MOS.ExtensionCommonCustomControlMenuItem();

      context.setCurrentMenuItemIndex(idx); // used for ribbons
      context.setCurrentContextMenuMenuControlMenuItemIndex(idx); // used for context menus
      this._populateMenuItem(context, menuItem, menuItemObject);

      if (Utilities.isNullOrUndefined(menuControl.items)) {
        menuControl.items = [];
      }

      menuControl.items.push(menuItem);
    }

    context.setCurrentMenuItemIndex(ConversionContext.UnsetIndex);
    context.setCurrentContextMenuMenuControlMenuItemIndex(ConversionContext.UnsetIndex);
  }

  private _populateMenuItem(
    context: ConversionContext,
    menuItemControl: MOS.ExtensionCommonCustomControlMenuItem,
    control: Object,
  ): void {
    let menuItemObject: Object = control[Constants.InputMenuItem];
    this._populateControlProperties(context, menuItemControl, control);
  }

  private _populateControlProperties(context: ConversionContext, mosControl: Object, control: Object): void {
    let isRibbon: boolean = context.isInRibbon();
    let isFixedControl: boolean = context.isInFixedControl();
    let isMenuItem: boolean = false;
    if (control[Constants.InputControlType] === Constants.InputButton) {
      let mosButton: MOS.ExtensionCommonCustomGroupControlsItem = <MOS.ExtensionCommonCustomGroupControlsItem>(
        mosControl
      );

      mosButton.enabled = control[Constants.InputEnabled];
      mosButton.id = control[Constants.InputId];
      mosButton.label = this._populateControlLocalizedField(
        context,
        control[Constants.InputLabelResId],
        control[Constants.InputLabel],
        Constants.MaxResourceStringLength64,
        ResourceType.AllLocaleShortStrings,
        isRibbon ? (isFixedControl ? Constants.RibbonsFixedControlsLabel : Constants.RibbonsTabsGroupsControlsLabel) : Constants.ContextMenusMenusControlsLabel,
      );
      mosButton.type = MOS.ExtensionCommonCustomGroupControlsItemType.Button;

      if (!context.isInFixedControl()) {
        mosButton.overriddenByRibbonApi = Utilities.isNullOrUndefined(control[Constants.InputOverridenByRibbonApi])
          ? false
          : control[Constants.InputOverridenByRibbonApi];
      }
    } else if (control[Constants.InputControlType] === Constants.InputMenuItem) {
      let mosMenuItem: MOS.ExtensionCommonCustomControlMenuItem = <MOS.ExtensionCommonCustomControlMenuItem>mosControl;

      mosMenuItem.enabled = control[Constants.InputEnabled];
      mosMenuItem.id = control[Constants.InputId];
      mosMenuItem.label = this._populateControlLocalizedField(
        context,
        control[Constants.InputLabelResId],
        control[Constants.InputLabel],
        Constants.MaxResourceStringLength64,
        ResourceType.AllLocaleShortStrings,
        isRibbon ? Constants.RibbonsTabsGroupsControlsItemsLabel : Constants.ContextMenusMenusControlsItemsLabel,
      );
      mosMenuItem.type = MOS.ExtensionCommonCustomControlMenuItemType.MenuItem;
      mosMenuItem.overriddenByRibbonApi = Utilities.isNullOrUndefined(control[Constants.InputOverridenByRibbonApi])
        ? false
        : control[Constants.InputOverridenByRibbonApi];

      isMenuItem = true;
    } else if (control[Constants.InputControlType] === Constants.InputMenu) {
      let mosMenu: MOS.ExtensionCommonCustomControlMenu = <MOS.ExtensionCommonCustomControlMenu>mosControl;

      mosMenu.id = control[Constants.InputId];
      mosMenu.label = this._populateControlLocalizedField(
        context,
        control[Constants.InputLabelResId],
        control[Constants.InputLabel],
        Constants.MaxResourceStringLength64,
        ResourceType.AllLocaleShortStrings,
        isRibbon ? Constants.RibbonsTabsGroupsControlsLabel : Constants.ContextMenusMenusControlsLabel,
      );
      mosMenu.type = MOS.ExtensionCommonCustomControlMenuType.Menu;
      mosMenu.overriddenByRibbonApi = Utilities.isNullOrUndefined(control[Constants.InputOverridenByRibbonApi])
        ? false
        : control[Constants.InputOverridenByRibbonApi];
    } else {
      Utilities.logError("Unrecognized control: " + control[Constants.InputControlType]);
      return;
    }

    // Menus have no action.  Only Buttons and MenuItems do.
    if (control[Constants.InputControlType] !== Constants.InputMenu) {
      this._populateControlAction(context, mosControl, control);
    }

    this._populateControlSupertip(context, mosControl, control);

    if (!Utilities.isNullOrUndefined(control[Constants.InputIcon])) {
      mosControl[Constants.MosIcons] = [];
      this._populateControlIcons(
        context,
        mosControl[Constants.MosIcons],
        control[Constants.InputIcon],
        control[Constants.InputIconResId],
        isMenuItem,
      );
    }
  }

  private _populateMobileControlProperties(
    context: ConversionContext,
    mosMobileControl: MOS.ExtensionRibbonsCustomMobileControlButtonItem,
    control: Object,
  ) {
    // Set mobile control properties
    mosMobileControl.type = MOS.ExtensionRibbonsCustomMobileControlButtonItemType.MobileButton;

    // If the label or labelResId exist then we try to set the label field and localized fields
    if (
      !Utilities.isNullOrUndefined(control[Constants.InputLabel]) ||
      !Utilities.isNullOrUndefined(control[Constants.InputLabelResId])
    ) {
      mosMobileControl.label = this._populateControlLocalizedField(
        context,
        control[Constants.InputLabelResId],
        control[Constants.InputLabel],
        Constants.MaxResourceStringLength32,
        ResourceType.AllLocaleShortStrings,
        Constants.RibbonsTabsCustomMobileRibbonGroupsControlsLabel,
      );
    }

    if (!Utilities.isNullOrUndefined(control[Constants.InputAction])) {
      this._populateControlAction(context, mosMobileControl, control);
    }

    if (!Utilities.isNullOrUndefined(control[Constants.InputId])) {
      mosMobileControl.id = control[Constants.InputId];
    } else {
      mosMobileControl.id = this._createUniqueId("mobileControl");
    }

    if (!Utilities.isNullOrUndefined(control[Constants.InputIcon])) {
      mosMobileControl.icons = [];
      this._populateMobileControlIcon(context, mosMobileControl.icons, control[Constants.InputIcon]);
    }
  }

  private _populateMobileControlIcon(
    context: ConversionContext,
    mosMobileControlIcons: MOS.ExtensionCustomMobileIcon[],
    icon: Object,
  ) {
    if (!Utilities.isNullOrUndefined(icon) && !Utilities.isNullOrUndefined(icon[Constants.InputImages])) {
      context.setCurrentIconIndex(0);

      for (var idx: number = 0; idx < icon[Constants.InputImages].length; idx++) {
        let image: Object = icon[Constants.InputImages][idx];
        let mosIcon: MOS.ExtensionCustomMobileIcon = new MOS.ExtensionCustomMobileIcon();

        if (!Utilities.isNullOrUndefined(image[Constants.InputScale])) {
          mosIcon.scale = Number(image[Constants.InputScale]);
        }

        if (!Utilities.isNullOrUndefined(image[Constants.InputSize])) {
          mosIcon.size = Number(image[Constants.InputSize]);
        }

        if (!Utilities.isNullOrUndefined(image[Constants.InputResid])) {
          mosIcon.url = this._getResourceAndWriteLocalizedValues(
            image[Constants.InputResid],
            null,
            Constants.MaxResourceStringLength2048,
            context.getCurrentVersionOverrideNumber(),
            Constants.RibbonsTabsCustomMobileRibbonGroupsControlsIconsUrl,
            ResourceType.AllLocaleImages,
            context.getCurrentRibbonIndex(),
            context.getCurrentTabIndex(),
            context.getCurrentCustomMobileGroupIndex(),
            context.getCurrentControlIndex(),
            context.getCurrentIconIndex(),
          );
        }

        mosMobileControlIcons.push(mosIcon);
        context.setCurrentIconIndex(context.getCurrentIconIndex() + 1);
      }
    }

    context.setCurrentIconIndex(ConversionContext.UnsetIndex);
  }

  private _populateControlLocalizedField(
    context: ConversionContext,
    labelResId: string,
    defaultValue: string,
    maxLength: number,
    resourceType: ResourceType,
    resourceString: string,
  ): string {
    if (context.isInRibbon()) {
      if (context.isInFixedControl()) {
        // If we are populating the ribbon inside of fixed controls
        return this._getResourceAndWriteLocalizedValues(
          labelResId,
          defaultValue,
          maxLength,
          context.getCurrentVersionOverrideNumber(),
          resourceString,
          resourceType,
          context.getCurrentRibbonIndex(),
          context.getCurrentFixedControlIndex(),
          ConversionContext.UnsetIndex,
          ConversionContext.UnsetIndex,
          ConversionContext.UnsetIndex,
        );        
      } else {
        // If we are populating the ribbon inside of tabs
        return this._getResourceAndWriteLocalizedValues(
          labelResId,
          defaultValue,
          maxLength,
          context.getCurrentVersionOverrideNumber(),
          resourceString,
          resourceType,
          context.getCurrentRibbonIndex(),
          context.getCurrentTabIndex(),
          context.getCurrentCustomMobileGroupIndex() == ConversionContext.UnsetIndex
            ? context.getCurrentGroupIndex()
            : context.getCurrentCustomMobileGroupIndex(),
          context.getCurrentControlIndex(),
          context.getCurrentMenuItemIndex(),
        );
      }
    } else {
      // If we are not populating the ribbon then we are populating a context menu
      return this._getResourceAndWriteLocalizedValues(
        labelResId,
        defaultValue,
        maxLength,
        context.getCurrentVersionOverrideNumber(),
        resourceString,
        resourceType,
        context.getCurrentContextMenuIndex(),
        context.getCurrentContextMenuMenuIndex(),
        context.getCurrentContextMenuMenuControlIndex(),
        context.getCurrentContextMenuMenuControlMenuItemIndex(),
        ConversionContext.UnsetIndex,
      );
    }
  }

  private _populateButtonControl(
    context: ConversionContext,
    buttonControl: MOS.ExtensionCommonCustomGroupControlsItem,
    control: Object,
  ): void {
    this._populateControlProperties(context, buttonControl, control);
  }

  private _populateControlAction(context: ConversionContext, mosControl: Object, control: Object): void {
    // Handle the action within the button
    if (!Utilities.isNullOrUndefined(control) && !Utilities.isNullOrUndefined(control[Constants.InputAction])) {
      let runtimeUrl: string = null;

      let functionName: string = control[Constants.InputAction][Constants.InputFunctionName];

      if (context.isInFixedControl()) {
        runtimeUrl = control[Constants.InputSourceLocationResid];
      } else if (!Utilities.isNullOrUndefined(control[Constants.InputAction][Constants.InputSourceLocationResid])) {
        runtimeUrl = control[Constants.InputAction][Constants.InputSourceLocationResid];
      } else if (!Utilities.isNullOrUndefined(functionName)) {
        runtimeUrl = context.getCurrentFunctionFileResid();
      }

      let runtime: MOS.Runtimes = null;
      // We need more implementation to look if the Id is available in the SharedRuntime array and check the Lifetime of it.
      let runtimeLookupKey: string = Converter._generateRuntimeLookupKey(
        runtimeUrl,
        MOS.RuntimesLifetime.Short,
        context.getCurrentVersionOverrideCapabilities(),
        context.getFormFactor()
      );
      if (!Utilities.isNullOrUndefined(runtimeLookupKey)) {
        runtime = this._runtimeLookup[runtimeLookupKey];
      }

      if (Utilities.isNullOrUndefined(runtime)) {
        Utilities.assert(!Utilities.isNullOrUndefined(runtime), "Expected runtime to be cached.");
        Utilities.logError("Did not find runtime. " + runtimeLookupKey);
        return;
      }

      let actionId: string = Constants.InputAction;

      // If this is an ExecuteFunction type with a function name then we use the function name as the action id
      if (!Utilities.isNullOrUndefined(functionName)) {
        actionId = functionName;
      } else {
        // If there is a taskpane id then we use it as a base so it is easier to identify the action
        // however we always append a unique integer to make sure that the action ids won't collide.
        if (!Utilities.isNullOrUndefined(control[Constants.InputAction][Constants.InputTaskpaneId])) {
          actionId = control[Constants.InputAction][Constants.InputTaskpaneId];
        }

        actionId = this._createUniqueActionId(actionId);
      }

      if (Utilities.isNullOrUndefined(runtime.actions)) {
        runtime.actions = [];
      }

      if (!Utilities.isNullOrUndefined(control[Constants.InputAction][Constants.InputSourceLocationResid])) {
        let action: MOS.ExtensionRuntimesActionsItem = new MOS.ExtensionRuntimesActionsItem();

        action.id = actionId;
        action.pinnable = control[Constants.InputAction][Constants.InputPinnable];
        if (Utilities.isNullOrUndefined(control[Constants.InputAction][Constants.InputTaskpaneId])) {
          action.view = this._createUniqueViewId(Constants.MosView);
        } else {
          action.view = control[Constants.InputAction][Constants.InputTaskpaneId];
        }

        action.displayName = actionId;
        action.type = MOS.ExtensionRuntimesActionsItemType.OpenPage;

        // TODO: Currently casting these and they still show up as "type=executeFunction" but the schema
        // currently doesn't allow the two types to be in the same runtime.  This is something that needs to be resolved.
        Utilities.log("Adding ShowTaskpane action: " + action.id);
        runtime.actions.push(action);
      } else if (!Utilities.isNullOrUndefined(control[Constants.InputAction][Constants.InputFunctionName])) {
        let action: MOS.ExtensionRuntimesActionsItem = new MOS.ExtensionRuntimesActionsItem();
        action.id = actionId;
        action.displayName = actionId;
        action.type = MOS.ExtensionRuntimesActionsItemType.ExecuteFunction;
        Utilities.log("Adding ExecuteAction action: " + action.id);
        runtime.actions.push(action);
      }

      // Finally link the control in the ribbon with the action inside of the runtime
      mosControl[Constants.MosActionId] = actionId;
    } else {
      Utilities.logError("Expected action and sourceLocation in button or menu control.");
    }
  }

  private _controlTypeFromString(controlType: string): MOS.ExtensionCommonCustomGroupControlsItemType {
    if (!Utilities.isNullOrUndefined(controlType)) {
      switch (controlType) {
        case Constants.InputButton:
          return MOS.ExtensionCommonCustomGroupControlsItemType.Button;
      }
    }
    throw "unable to find control type:" + controlType;
  }

  private _populateControlIcons(
    context: ConversionContext,
    mosControlIcons: MOS.ExtensionCommonIcon[],
    icons: [],
    iconResIds: [],
    isMenuItem: boolean,
  ): void {
    if (Utilities.isNullOrUndefined(icons)) {
      return;
    }

    let iconIdx: number = 0;

    for (let size in icons) {
      const url: string = icons[size];
      const resId: string = iconResIds[size];

      context.setCurrentIconIndex(iconIdx);

      const mosIcon: MOS.ExtensionCommonIcon = new MOS.ExtensionCommonIcon();

      let fileShortName: string = this._iconBuilder.addIconAndGetReference(url, !Utilities._writeImageUrls, false);

      if (context.isInRibbon()) {
        if (!context.isInFixedControl()) {
          if (isMenuItem) {
            this._getResourceAndWriteLocalizedValues(
              resId,
              fileShortName,
              Constants.MaxResourceStringLength2048,
              context.getCurrentVersionOverrideNumber(),
              Utilities._writeImageUrls
                ? Constants.RibbonsTabsGroupsControlsItemsIconsUrl
                : Constants.RibbonsTabsGroupsControlsItemsIconsFile,
              ResourceType.AllLocaleImages,
              context.getCurrentRibbonIndex(),
              context.getCurrentTabIndex(),
              context.getCurrentGroupIndex(),
              context.getCurrentControlIndex(),
              context.getCurrentMenuItemIndex(),
              context.getCurrentIconIndex(),
            );
          } else {
            this._getResourceAndWriteLocalizedValues(
              resId,
              fileShortName,
              Constants.MaxResourceStringLength2048,
              context.getCurrentVersionOverrideNumber(),
              Utilities._writeImageUrls
                ? Constants.RibbonsTabsGroupsControlsIconsUrl
                : Constants.RibbonsTabsGroupsControlsIconsFile,
              ResourceType.AllLocaleImages,
              context.getCurrentRibbonIndex(),
              context.getCurrentTabIndex(),
              context.getCurrentGroupIndex(),
              context.getCurrentControlIndex(),
              context.getCurrentIconIndex(),
            );
          }
        } else {
          this._getResourceAndWriteLocalizedValues(
            resId,
            fileShortName,
            Constants.MaxResourceStringLength2048,
            context.getCurrentVersionOverrideNumber(),
            Constants.RibbonsFixedControlsIconsUrl,
            ResourceType.AllLocaleImages,
            context.getCurrentRibbonIndex(),
            context.getCurrentFixedControlIndex(),
            context.getCurrentIconIndex(),
            ConversionContext.UnsetIndex,
            ConversionContext.UnsetIndex
          );
        }
      } else {
        // If we are populating a context menu

        if (isMenuItem) {
          this._getResourceAndWriteLocalizedValues(
            resId,
            fileShortName,
            Constants.MaxResourceStringLength2048,
            context.getCurrentVersionOverrideNumber(),
            Utilities._writeImageUrls
              ? Constants.ContextMenusMenusControlsItemsIconsUrl
              : Constants.ContextMenusMenusControlsItemsIconsFile,
            ResourceType.AllLocaleImages,
            context.getCurrentContextMenuIndex(),
            context.getCurrentContextMenuMenuIndex(),
            context.getCurrentContextMenuMenuControlIndex(),
            context.getCurrentContextMenuMenuControlMenuItemIndex(),
            context.getCurrentIconIndex(),
            ConversionContext.UnsetIndex,
          );
        } else {
          this._getResourceAndWriteLocalizedValues(
            resId,
            fileShortName,
            Constants.MaxResourceStringLength2048,
            context.getCurrentVersionOverrideNumber(),
            Utilities._writeImageUrls
              ? Constants.ContextMenusMenusControlsIconsUrl
              : Constants.ContextMenusMenusControlsIconsFile,
            ResourceType.AllLocaleImages,
            context.getCurrentContextMenuIndex(),
            context.getCurrentContextMenuMenuIndex(),
            context.getCurrentContextMenuMenuControlIndex(),
            context.getCurrentIconIndex(),
            ConversionContext.UnsetIndex,
          );
        }
      }

      // formerly there was an else{} with mosIcon.file for the case when the icons are in the package
      mosIcon.url = fileShortName;

      mosIcon.size = +size;
      mosControlIcons.push(mosIcon);

      iconIdx++;
    }
  }

  private _populateControlSupertip(context: ConversionContext, mosControl: Object, control: Object): void {
    let supertip: Object = control[Constants.InputSupertip];
    // Only create the supertip if the legacy control has one
    if (Utilities.isNullOrUndefined(supertip)) {
      return;
    }

    let isRibbon: boolean = context.isInRibbon();
    let isFixedControl: boolean = context.isInFixedControl();

    if (mosControl[Constants.MosType] === MOS.ExtensionCommonCustomGroupControlsItemType.Button) {
      let mosButton: MOS.ExtensionCommonCustomGroupControlsItem = <MOS.ExtensionCommonCustomGroupControlsItem>(
        mosControl
      );
      mosButton.supertip = new MOS.ExtensionCommonSuperToolTip();
      mosButton.supertip.description = this._populateControlLocalizedField(
        context,
        supertip[Constants.InputDescriptionResId],
        supertip[Constants.InputDescription],
        Constants.MaxResourceStringLength128,
        ResourceType.AllLocaleLongStrings,
        isRibbon ? (isFixedControl ? Constants.RibbonsFixedControlsSuperTipDescription : Constants.RibbonsTabsGroupsControlsSupertipDescription)
          : Constants.ContextMenusMenusControlsSupertipDescription,
      );
      mosButton.supertip.title = this._populateControlLocalizedField(
        context,
        supertip[Constants.InputTitleResId],
        supertip[Constants.InputTitle],
        Constants.MaxResourceStringLength64,
        ResourceType.AllLocaleShortStrings,
        isRibbon ? (isFixedControl ? Constants.RibbonsFixedControlsSuperTipTitle : Constants.RibbonsTabsGroupsControlsSupertipTitle) : Constants.ContextMenusMenusControlsSupertipTitle,
      );
    } else if (mosControl[Constants.MosType] === MOS.ExtensionCommonCustomControlMenuItemType.MenuItem) {
      let mosMenuItem: MOS.ExtensionCommonCustomControlMenuItem = <MOS.ExtensionCommonCustomControlMenuItem>mosControl;
      mosMenuItem.supertip = new MOS.ExtensionCommonSuperToolTip();

      mosMenuItem.supertip.description = this._populateControlLocalizedField(
        context,
        supertip[Constants.InputDescriptionResId],
        supertip[Constants.InputDescription],
        Constants.MaxResourceStringLength128,
        ResourceType.AllLocaleLongStrings,
        isRibbon
          ? Constants.RibbonsTabsGroupsControlsItemsSupertipDescription
          : Constants.ContextMenusMenusControlsItemsSupertipDescription,
      );
      mosMenuItem.supertip.title = this._populateControlLocalizedField(
        context,
        supertip[Constants.InputTitleResId],
        supertip[Constants.InputTitle],
        Constants.MaxResourceStringLength64,
        ResourceType.AllLocaleShortStrings,
        isRibbon
          ? Constants.RibbonsTabsGroupsControlsItemsSupertipTitle
          : Constants.ContextMenusMenusControlsItemsSupertipTitle,
      );
    } else if (mosControl[Constants.MosType] === MOS.ExtensionCommonCustomControlMenuType.Menu) {
      let mosMenu: MOS.ExtensionCommonCustomControlMenu = <MOS.ExtensionCommonCustomControlMenu>mosControl;
      mosMenu.supertip = new MOS.ExtensionCommonSuperToolTip();

      mosMenu.supertip.description = this._populateControlLocalizedField(
        context,
        supertip[Constants.InputDescriptionResId],
        supertip[Constants.InputDescription],
        Constants.MaxResourceStringLength128,
        ResourceType.AllLocaleLongStrings,
        isRibbon
          ? Constants.RibbonsTabsGroupsControlsSupertipDescription
          : Constants.ContextMenusMenusControlsSupertipDescription,
      );
      mosMenu.supertip.title = this._populateControlLocalizedField(
        context,
        supertip[Constants.InputTitleResId],
        supertip[Constants.InputTitle],
        Constants.MaxResourceStringLength64,
        ResourceType.AllLocaleShortStrings,
        isRibbon ? Constants.RibbonsTabsGroupsControlsSupertipTitle : Constants.ContextMenusMenusControlsSupertipTitle,
      );
    } else {
      Utilities.logError("Unexpected control: " + control[Constants.InputControlType]);
    }
  }

  private _addScopesFromHosts(requirements: MOS.RequirementsExtensionElement): void {
    requirements.scopes = [];
    // Get the different hosts specified in the top level <Hosts> node and
    // add the corresponding hosts on the runtime.
    for (let host in this._xmlManifest.getHosts()) {
      switch (this._xmlManifest.getHosts()[host]) {
        case OSF.ManifestHostType.Workbook:
          requirements.scopes.push(MOS.Scopes.Workbook);
          break;
        case OSF.ManifestHostType.Document:
          requirements.scopes.push(MOS.Scopes.Document);
          break;
        case OSF.ManifestHostType.Presentation:
          requirements.scopes.push(MOS.Scopes.Presentation);
          break;
        case OSF.ManifestHostType.Notebook:
          // TODO: currently don't have notebook scope in mos manifest
          Utilities.assert(false, "need to implement notebook scope");
          break;
        case OSF.ManifestHostType.Mailbox:
          requirements.scopes.push(MOS.Scopes.Mail);
          break;
        case OSF.ManifestHostType.Drawing:
          // TODO: currently don't have notebook scope in mos manifest
          Utilities.assert(false, "need to implement drawing scope");
          break;
      }
    }

    if (requirements.scopes.length == 0) {
      // If there is no <Hosts> node that tells us what hosts this addin is for, for Outlook/Mailapp, we can try to fall back
      // on the manifest type which is on the <OfficeApp type=""> node.  This will not work for WXP but will for outlook because
      // there is a one to one mapping from MailApp to the host Outlook that it works in and applies to.
      if (
        !Utilities.isNullOrUndefined(this._xmlManifest._hostType) &&
        this._xmlManifest.getTarget() == OSF.OfficeAppType.MailApp
      ) {
        requirements.scopes.push(MOS.Scopes.Mail);
      }
    }
  }

  private _populateRuntimes(context: ConversionContext, extension: MOS.Extensions): void {
    let runtimes: MOS.Runtimes[] = [];
    let contentRuntimes: MOS.ContentRuntimes[] = [];

    // Go through and collect all the different runtimes that are in the manifest
    // in default settings, runtimes nodes, actions etc. There will be one runtime for each unique url

    let collectedRuntimeRecords = [];
    this._collectRuntimes(context, collectedRuntimeRecords);

    this._runtimeLookup = {};
    this._createMosRuntimesFromRuntimeRecords(collectedRuntimeRecords, /*out*/ runtimes, /*out*/ contentRuntimes, this._runtimeLookup);

    if (runtimes.length > 0) {
      extension.runtimes = runtimes;
    }

    if (contentRuntimes.length > 0) {
      extension.contentRuntimes = contentRuntimes;
    }
  }

  // The purpose of this method is to coalesce the runtime records from the possible runtimes that we've found into a set of
  // runtimes that will be used for the remainder of the parsing and where things like actions and functions can be added.
  private _createMosRuntimesFromRuntimeRecords(
    runtimeRecords: RuntimeRecord[],
    runtimes: MOS.Runtimes[],
    contentRuntimes: MOS.ContentRuntimes[],
    runtimeLookup: Object,
  ): void {

    if (runtimeRecords.length == 0) {
      // no runtime detected. log an error
      console.error(
        "\x1b[31m%s\x1b[0m",
        'No supported runtime found in "' +
          Utilities._inputXmlFilePath +
          '", so the converted manifest is invalid. ' +
          "Please note that some elements such as ContentPane and DetectedEntity are not supported by metaOS manifest.",
      );
      process.exitCode = 1;
    }

    // The index of the runtimes that are created. Used to properly create the localization strings like: "extension[0].runtimes[3]"
    let idxRuntime: number = -1;
    let idxContentRuntime: number = -1;

    // We loop through these runtimes and create unique mosruntimes for each unique sourceLocation/Resid
    for (let idxRuntimeRecord: number = 0; idxRuntimeRecord < runtimeRecords.length; idxRuntimeRecord++) {
      let runtimeRecord: RuntimeRecord = runtimeRecords[idxRuntimeRecord];

      // Content runtimes are only supported for content apps and regular runtimes are only supported for TaskPane and MailApps
      if (runtimeRecord._source == RuntimeSource.DefaultSettingSourceLocations) {
        if (this._xmlManifest.getTarget() !== OSF.OfficeAppType.ContentApp) {
          continue;
        }
      } else {
        if (this._xmlManifest.getTarget() === OSF.OfficeAppType.ContentApp) {
          continue;
        }
      }

      // Create runtime lookup key
      let key: string = runtimeRecord.getKey();

      // Based on the key we lookup the Runtime in the table if it is not added that means we need to create the corrosponding Runtime object in the MOS manifest
      // If it is already present then we should skip it and continue iterating the loop
      // This runtime is either a MOS.Runtimes or a MOS.ContentRuntimes
      let rt: object = runtimeLookup[key];

      if (Utilities.isNullOrUndefined(rt)) {

        if (runtimeRecord._source == RuntimeSource.DefaultSettingSourceLocations) {
          rt = new MOS.ContentRuntimes();
          idxContentRuntime++;
        } else {
          rt = new MOS.Runtimes();
          idxRuntime++;
        }

        rt[Constants.MosId] = "runtime_" + this.getNextRuntimeSuffix();

        rt[Constants.MosRequirements] = Converter._cloneRequirements(runtimeRecord._requirements);
        rt[Constants.MosRequirements].capabilities = runtimeRecord._versionOverrideCapabilities;

        if (!Utilities.isNullOrUndefined(runtimeRecord._inputFormFactor)) {

          let formFactor: MOS.FormFactors = Converter._legacyFormFactorToMOSFormFactor(runtimeRecord._inputFormFactor);

          if (Utilities.isNullOrUndefined(rt[Constants.MosRequirements])) {
            rt[Constants.MosRequirements] = new MOS.RequirementsExtensionElement();
          }

          if (Utilities.isNullOrUndefined(rt[Constants.MosRequirements].formFactors)) {
            rt[Constants.MosRequirements].formFactors = [];
          }
          rt[Constants.MosRequirements].formFactors.push(formFactor);
        }

        // All of these are regular runtimes
        if (
          runtimeRecord._source == RuntimeSource.ShowTaskpane ||
          runtimeRecord._source == RuntimeSource.ExecuteFunction ||
          runtimeRecord._source == RuntimeSource.SharedRuntimes ||
          runtimeRecord._source == RuntimeSource.LaunchEvent ||
          runtimeRecord._source == RuntimeSource.ReportPhishing
        ) {

          let runtime: MOS.Runtimes = <MOS.Runtimes>(<unknown>rt);

          runtime.type = MOS.RuntimesType.General;
          runtime.lifetime = runtimeRecord._lifetime;

          runtime.code.page = this._getResourceAndWriteLocalizedValues(
            runtimeRecord._resource,
            null,
            Constants.MaxResourceStringLength2048,
            runtimeRecord._versionOverridesNumber,
            Constants.RuntimesPage,
            ResourceType.AllLocaleUrls,
            idxRuntime,
          );
          if (!Utilities.isNullOrUndefined(runtimeRecord._scriptResId)) {
            runtime.code.script = this._getResourceAndWriteLocalizedValues(
              runtimeRecord._scriptResId,
              null,
              Constants.MaxResourceStringLength2048,
              runtimeRecord._versionOverridesNumber,
              Constants.RuntimesScript,
              ResourceType.AllLocaleUrls,
              idxRuntime,
            );
          }
        } else if (runtimeRecord._source == RuntimeSource.DefaultSettingSourceLocations) {

          let contentRuntime: MOS.ContentRuntimes = <MOS.ContentRuntimes>(<unknown>rt);

          // DefaultSettings are in content apps and are used for in content add ins/agaves and are content runtimes
          contentRuntime.disableSnapshot = !this._xmlManifest.isAllowSnapshot();

          // We only support mobile and desktop
          let osfFormFactor: OSF.FormFactor = runtimeRecord._inputFormFactor === Constants.InputMobileFormFactor ? OSF.FormFactor.Phone : OSF.FormFactor.Desktop;

          let defaultWidth: any = this._xmlManifest.getDefaultWidth(osfFormFactor);
          let defaultHeight: any = this._xmlManifest.getDefaultHeight(osfFormFactor);

          if (!Utilities.isNullOrUndefined(defaultWidth)) {
            contentRuntime.requestedWidth = +defaultWidth;
          }

          if (!Utilities.isNullOrUndefined(defaultHeight)) {
            contentRuntime.requestedHeight = +defaultHeight;
          }

          // DefaultSettings are outside of VersionOverrides so they have the translations in <Override> nodes and these
          // are stored in the runtimeRecord._localResources property.  Therefore we don't do a resId lookup here (which is VersionOverride mechanism)
          // but instead look up the proper language string in runtimeRecord._localeResources.
          if (!Utilities.isNullOrUndefined(runtimeRecord._localeResources[this._xmlManifest.getDefaultLocale()])) {
            contentRuntime[Constants.MosCode].page = runtimeRecord._localeResources[this._xmlManifest.getDefaultLocale()];
          } else if (!Utilities.isNullOrUndefined(runtimeRecord._resource)) {
            contentRuntime[Constants.MosCode].page = runtimeRecord._resource;
          }
        } else {
          Utilities.logError(
            "Unrecognized runtimerecord._source: " + runtimeRecord._source + "runtime.source: " + runtimeRecord._source,
          );
        }

        Utilities.assert(!Utilities.isNullOrUndefined(rt[Constants.MosCode].page), "runtime.code.page should not be null");

        // add this runtime to the MOS manifest in the appropriate location
        if (runtimeRecord._source == RuntimeSource.DefaultSettingSourceLocations) {
          contentRuntimes.push(<MOS.ContentRuntimes>(<unknown>rt));
        } else {
          runtimes.push(<MOS.Runtimes>(<unknown>rt));
        }

        // Add a lookup to this runtime so that it can easily be found later when parsing the rest of the manifest
        // And finding things like Actions that need to be assigned to a particular runtime that matches their source location url.
        runtimeLookup[key] = rt;

        // Now create the localized strings for this runtime
        if (!Utilities.isNullOrUndefined(runtimeRecord._localeResources)) {
          for (let locale in runtimeRecord._localeResources) {
            // The default locale values are written into the main manifest file and do not need separate localized resources in another file
            if (locale == this._xmlManifest.getDefaultLocale()) {
              continue;
            }

            let resourceTemplate = "";

            if (!Utilities.isNullOrUndefined(rt[Constants.MosCode].page)) {

              let resourceName: string = null;

              // Now add a localized resource like runtimes[1].code.page or contentRuntimes[1].code.script
              if (runtimeRecord._source == RuntimeSource.DefaultSettingSourceLocations) {
                resourceTemplate = Constants.ContentRuntimesPage;
                resourceName = util.format(resourceTemplate, idxContentRuntime);
              } else {
                resourceTemplate = Constants.RuntimesPage;
                resourceName = util.format(resourceTemplate, idxRuntime);
              }

              this._localeBuilder.addLocaleString(locale, resourceName, runtimeRecord._localeResources[locale]);
            }

            // code.script is not set for ContentApps so we only need to handle it for general runtimes here (ie extension[0].runtimes and not extension[0].contentRuntimes)
            if (!Utilities.isNullOrUndefined(rt[Constants.MosCode].script)) {
              resourceTemplate = Constants.RuntimesScript;
              // Now add a localized resource like runtimes[1].code.script
              let resourceName: string = util.format(resourceTemplate, idxRuntime);
              this._localeBuilder.addLocaleString(locale, resourceName, runtimeRecord._localeResources[locale]);
            }
          }
        }
      }
    }
  }

  private _collectRuntimes(context: ConversionContext, collectedRuntimes: RuntimeRecord[]): void {
    // Get runtimes from default settings
    // Loop over form factors and add runtime for each:  "mobile", "desktop", "tablet" etc.
    for (let formFactor in OSF.FormFactor) {
      try {
        let formFactorsSourceLocation: string = this._xmlManifest.getDefaultSourceLocation(formFactor);

        // If there is a source location for this form factor then we create a runtime for it
        if (!Utilities.isNullOrUndefined(formFactorsSourceLocation)) {
          context.setFormFactor(Converter._oSFFormFactorToInputFormFactor(formFactor));
          this._addRuntimeForFormFactor(context, collectedRuntimes, formFactor, formFactorsSourceLocation);
          context.setFormFactor(null);
        }
      } catch {
        // xmlManifest throws when there are no default settings at all
        continue;
      }
    }

    // Get runtime records from <Runtimes> for each type of app (in the legacy manifest, app like document, workbook etc. is also
    // referred to as "capability" but this is not to be confused with "capability" in the new mos manifest which is part of the
    // requirements and is essentially means what features are supported on the host.
    for (let manifestHostType in OSF.ManifestHostType) {
      if (
        manifestHostType === OSF.ManifestHostType.Mailbox ||
        manifestHostType === OSF.ManifestHostType.Document ||
        manifestHostType === OSF.ManifestHostType.Presentation ||
        manifestHostType === OSF.ManifestHostType.Workbook
      ) {
        // By default the cabability string is also the overrides lookup property
        let hostType: string = Converter._getVersionOverrideHostTypeFromOsfManifestHostType(manifestHostType);

        if (!Utilities.isNullOrUndefined(this._xmlManifest._versionOverrides[hostType])) {
          // Handle WXP VersionOverride
          if (Converter._isWXP(hostType)) {
            // Set the capabilities (legacy requirement sets) in the context so that this can be set propertly in the runtime records
            Converter._setVersionOverrideNumberAndCapabilitiesInContext(
              context,
              this._xmlManifest,
              hostType,
              OSF.VersionOverridesNumber.V1_0.toString(),
            );

            // Currently WXP is only handling the desktop form factor
            context.setFormFactor(Constants.InputDesktopFormFactor);

            if (!Utilities.isNullOrUndefined(this._xmlManifest._versionOverrides[hostType]._runtimes)) {
              this._collectRuntimeRecordsFromRuntimes(
                context,
                collectedRuntimes,
                this._xmlManifest._versionOverrides[hostType]._runtimes,
                manifestHostType,
                RuntimeSource.SharedRuntimes,
              );
            }
            context.setFormFactor(null);

            // First look for information under the override
            let addinCommandsExtensionPoints: [] = Converter._getAddinCommandsExtensionPoints(
              hostType,
              null,
              Constants.InputDesktopFormFactor,
              this._xmlManifest,
            );

            let functionFileResid: Object = Converter._getFunctionFileResid(hostType, null, this._xmlManifest);

            if (!Utilities.isNullOrUndefined(functionFileResid)) {
              this._collectRuntimesFromFunctionFiles(context, functionFileResid, collectedRuntimes, manifestHostType);
            }

            if (!Utilities.isNullOrUndefined(addinCommandsExtensionPoints)) {
              context.setFormFactor(Constants.InputDesktopFormFactor);
              this._collectRuntimeRecordsFromExtensionPoints(
                context,
                addinCommandsExtensionPoints,
                collectedRuntimes,
                manifestHostType,
              );
              context.setFormFactor(null);
            }

            // Reset this in the context since we are done loading these from the overrides
            Converter._clearVersionOverrideNumberAndCapabilitiesInContext(context);

            // Reset form factor in the context
            context.setFormFactor(null);
          }
          // Outlook VersionOverride case
          else if (hostType === Constants.InputMailHost) {
            let supportedVersionOverrides = Converter._getMailHostVersionOVerrides();

            // Now look for extension points in each override
            for (let vo of supportedVersionOverrides) {
              // Set the capabilities (legacy requirement sets) in the context so that this can be set propertly in the runtime records
              Converter._setVersionOverrideNumberAndCapabilitiesInContext(context, this._xmlManifest, hostType, vo);

              if (!Utilities.isNullOrUndefined(this._xmlManifest._versionOverrides[hostType][vo])) {
                // Get runtimes from <Runtime> nodes

                // <Runtimes> node is used in the shared runtimes feature and this is only applicable for desktop
                context.setFormFactor(Constants.InputDesktopFormFactor);
                if (!Utilities.isNullOrUndefined(this._xmlManifest._versionOverrides[hostType][vo]._runtimes)) {
                  this._collectRuntimeRecordsFromRuntimes(
                    context,
                    collectedRuntimes,
                    this._xmlManifest._versionOverrides[hostType][vo]._runtimes,
                    manifestHostType,
                    RuntimeSource.SharedRuntimes,
                  );
                }
                context.setFormFactor(null);

                let addinCommandsExtensionPointsVersionsDesktop: [] = Converter._getAddinCommandsExtensionPoints(
                  hostType,
                  vo,
                  Constants.InputDesktopFormFactor,
                  this._xmlManifest,
                );

                let addinCommandsExtensionPointsVersionsMobile: [] = Converter._getAddinCommandsExtensionPoints(
                  hostType,
                  vo,
                  Constants.InputMobileFormFactor,
                  this._xmlManifest,
                );

                let functionFileResidVersion: Object = Converter._getFunctionFileResid(hostType, vo, this._xmlManifest);

                if (!Utilities.isNullOrUndefined(functionFileResidVersion)) {
                  this._collectRuntimesFromFunctionFiles(
                    context,
                    functionFileResidVersion,
                    collectedRuntimes,
                    manifestHostType,
                  );
                }

                if (!Utilities.isNullOrUndefined(addinCommandsExtensionPointsVersionsDesktop)) {
                  context.setFormFactor(Constants.InputDesktopFormFactor);
                  this._collectRuntimeRecordsFromExtensionPoints(
                    context,
                    addinCommandsExtensionPointsVersionsDesktop,
                    collectedRuntimes,
                    manifestHostType,
                  );
                  context.setFormFactor(null);
                }

                if (!Utilities.isNullOrUndefined(addinCommandsExtensionPointsVersionsMobile)) {
                  context.setFormFactor(Constants.InputMobileFormFactor);
                  this._collectRuntimeRecordsFromExtensionPoints(
                    context,
                    addinCommandsExtensionPointsVersionsMobile,
                    collectedRuntimes,
                    manifestHostType,
                  );
                  context.setFormFactor(null);
                }

                // Reset this in the context since we are done loading these from the overrides
                Converter._clearVersionOverrideNumberAndCapabilitiesInContext(context);
              }
            }
          }
        }
      }
    }
  }

  private _collectRuntimeRecordsFromExtensionPoints(
    context: ConversionContext,
    addinCommandsExtensionPoints: [],
    collectedRuntimes: RuntimeRecord[],
    manifestHostType: OSF.ManifestHostType,
  ): void {
    for (
      let extensionPointIdx: number = 0;
      extensionPointIdx < addinCommandsExtensionPoints.length;
      extensionPointIdx++
    ) {
      let extensionPoint: Object = addinCommandsExtensionPoints[extensionPointIdx];
      if (Converter._isRibbonCommandSurface(extensionPoint[Constants.InputType])) {
        if (!Utilities.isNullOrUndefined(extensionPoint[Constants.InputTabs])) {
          for (let tabIdx: number = 0; tabIdx < extensionPoint[Constants.InputTabs].length; tabIdx++) {
            var tab: Object = extensionPoint[Constants.InputTabs][tabIdx];
            if (!Utilities.isNullOrUndefined(tab[Constants.InputChildren])) {
              for (let groupIdx: number = 0; groupIdx < tab[Constants.InputChildren].length; groupIdx++) {
                var group: Object = tab[Constants.InputChildren][groupIdx];

                if (
                  !Utilities.isNullOrUndefined(group) &&
                  !Utilities.isNullOrUndefined(group[Constants.InputChildren])
                ) {
                  this._collectRuntimeRecordsFromActions(
                    context,
                    collectedRuntimes,
                    group[Constants.InputChildren],
                    manifestHostType,
                  );
                }
              }
            }
          }
        }
      } else if (Converter._isMobileCommandSurface(extensionPoint[Constants.InputType])) {
        var control: any;
        if (
          !Utilities.isNullOrUndefined(extensionPoint[Constants.InputGroup]) &&
          !Utilities.isNullOrUndefined(extensionPoint[Constants.InputGroup][Constants.InputControl])
        ) {
          control = extensionPoint[Constants.InputGroup][Constants.InputControl];
        } else if (!Utilities.isNullOrUndefined(extensionPoint[Constants.InputControl])) {
          control = extensionPoint[Constants.InputControl];
        }

        if (Utilities.isNullOrUndefined(control)) {
          Utilities.logError("Unable to find <Control> inside of mobile form factor.");
          continue;
        }

        let requirements: MOS.RequirementsExtensionElement = new MOS.RequirementsExtensionElement();

        requirements.capabilities = context.getCurrentVersionOverrideCapabilities();

        // Here we only care about the ShowTaskPane actions because the function file runtime
        // is added in another place and it is shared by many ExecuteAction actions.
        if (
          control.actionType == Constants.InputShowTaskpane &&
          !Utilities.isNullOrUndefined(control.action.sourceLocationResid)
        ) {
          let runtimeRecord: RuntimeRecord = this._createRuntimeRecord(
            context,
            control.action.sourceLocationResid,
            requirements,
            RuntimeSource.ShowTaskpane,
            manifestHostType,
          );

          this._addRuntimeRecord(runtimeRecord, collectedRuntimes);
        }
      } else if (Converter._isContextMenuCommandSurface(extensionPoint[Constants.InputType])) {
        if (!Utilities.isNullOrUndefined(extensionPoint[Constants.InputChildren])) {
          for (
            let menuNodeIdx: number = 0;
            menuNodeIdx < extensionPoint[Constants.InputChildren].length;
            menuNodeIdx++
          ) {
            const menuNode: any = extensionPoint[Constants.InputChildren][menuNodeIdx];

            for (
              let menuControlIdx: number = 0;
              menuControlIdx < menuNode[Constants.InputChildren].length;
              menuControlIdx++
            ) {
              const menuControl: any = menuNode[Constants.InputChildren][menuControlIdx];
              this._collectRuntimeRecordsFromActions(
                context,
                collectedRuntimes,
                menuControl[Constants.InputChildren],
                manifestHostType,
              );
            }
          }
        }
      } else if (extensionPoint[Constants.InputType] === Constants.InputLaunchEvent) {
        if (!Utilities.isNullOrUndefined(extensionPoint[Constants.InputResid])) {
          let requirements: MOS.RequirementsExtensionElement = new MOS.RequirementsExtensionElement();
          requirements.capabilities = context.getCurrentVersionOverrideCapabilities();
          let runtimeRecord: RuntimeRecord = this._createRuntimeRecord(
            context,
            extensionPoint[Constants.InputResid],
            requirements,
            RuntimeSource.LaunchEvent,
            manifestHostType,
          );
          this._addRuntimeRecord(runtimeRecord, collectedRuntimes);
        } else {
          Utilities.logWarning("LaunchEvent extension point does not have SourceLocation with resid.");
        }
      } else if (extensionPoint[Constants.InputType] === Constants.InputReportPhishingCommandSurface) {
        if (!Utilities.isNullOrUndefined(extensionPoint[Constants.InputFixedControl]) &&
            !Utilities.isNullOrUndefined(extensionPoint[Constants.InputFixedControl][Constants.InputSourceLocationResid])) {
          let requirements: MOS.RequirementsExtensionElement = new MOS.RequirementsExtensionElement();
          requirements.capabilities = context.getCurrentVersionOverrideCapabilities();

          let sourceLocationResId: string = null;
          if (!Utilities.isNullOrUndefined(extensionPoint[Constants.InputFixedControl]) &&
          !Utilities.isNullOrUndefined(extensionPoint[Constants.InputFixedControl][Constants.InputSourceLocationResid])) {
            sourceLocationResId = extensionPoint[Constants.InputFixedControl][Constants.InputSourceLocationResid];
          }

          let runtimeRecord: RuntimeRecord = this._createRuntimeRecord(
            context,
            sourceLocationResId,
            requirements,
            RuntimeSource.ReportPhishing,
            manifestHostType,
          );
          this._addRuntimeRecord(runtimeRecord, collectedRuntimes);
        } else {
          Utilities.logWarning("ReportPhishingCustomization extension point does not have SourceLocation with resid.");
        }
      }
      else {
        Utilities.log("Skipping extension point %s for runtime collection.", extensionPoint[Constants.InputType]);
      }
    }
  }

  private _collectRuntimesFromFunctionFiles(
    context: ConversionContext,
    functionFileResid: Object,
    collectedRuntimes: RuntimeRecord[],
    manifestHostType: OSF.ManifestHostType,
  ): void {
    if (!Utilities.isNullOrUndefined(functionFileResid)) {
      if (!Utilities.isNullOrUndefined(functionFileResid[Constants.InputDesktopFormFactor])) {
        context.setFormFactor(Constants.InputDesktopFormFactor);
        this._addRuntimeRecordForFunctionFileFormFactor(
          context,
          functionFileResid[Constants.InputDesktopFormFactor],
          Constants.InputDesktopFormFactor,
          collectedRuntimes,
          manifestHostType,
        );
        context.setFormFactor(null);
      }

      if (!Utilities.isNullOrUndefined(functionFileResid[Constants.InputMobileFormFactor])) {
        context.setFormFactor(Constants.InputMobileFormFactor);
        this._addRuntimeRecordForFunctionFileFormFactor(
          context,
          functionFileResid[Constants.InputMobileFormFactor],
          Constants.InputMobileFormFactor,
          collectedRuntimes,
          manifestHostType,
        );
        context.setFormFactor(null);
      }
    }
  }

  private _addRuntimeRecordForFunctionFileFormFactor(
    context: ConversionContext,
    resid: string,
    formFactor: string,
    collectedRuntimes: RuntimeRecord[],
    manifestHostType: OSF.ManifestHostType,
  ): void {
    let requirements: MOS.RequirementsExtensionElement = new MOS.RequirementsExtensionElement();
    requirements.capabilities = context.getCurrentVersionOverrideCapabilities();

    let runtimeRecord: RuntimeRecord = this._createRuntimeRecord(
      context,
      resid,
      requirements,
      RuntimeSource.ExecuteFunction,
      manifestHostType,
    );

    // TODO: Need to set lifetime.long for WXP if the same resID found in Runtimes element

    this._addRuntimeRecord(runtimeRecord, collectedRuntimes);
  }

  private static _legacyFormFactorToMOSFormFactor(formFactor: string): MOS.FormFactors {
    switch (formFactor) {
      case Constants.InputDesktopFormFactor:
        return MOS.FormFactors.Desktop;
      case Constants.InputMobileFormFactor:
        return MOS.FormFactors.Mobile;
      default:
        Utilities.logError("Unrecognized formfactor: " + formFactor);
        return null;
    }
  }

  private static _isRibbonCommandSurface(commandSurface: string): boolean {
    // The commandSurfaces that we currently look for and convert in runtimes and ribbons
    return (
      commandSurface === Constants.InputPrimaryCommandSurface ||
      commandSurface === Constants.InputMessageComposeCommandSurface ||
      commandSurface === Constants.InputMessageReadCommandSurface ||
      commandSurface === Constants.InputAppointmentOrganizerCommandSurface ||
      commandSurface === Constants.InputAppointmentAttendeeCommandSurface
    );
  }

  private static _isContextMenuCommandSurface(commandSurface: string): boolean {
    // The commandSurfaces that we currently look for and convert in runtimes and ribbons
    return commandSurface === Constants.InputContextMenuCommandSurface;
  }

  private static _isMobileCommandSurface(commandSurface: string): boolean {
    // The commandSurfaces that we currently look for and convert for mobile
    return (
      commandSurface === Constants.InputMobileMessageReadCommandSurface ||
      commandSurface === Constants.InputMobileMessageComposeCommandSurface ||
      commandSurface === Constants.InputMobileAppointmentOrganizerCommandSurface ||
      commandSurface === Constants.InputMobileAppointmentAttendeeCommandSurface ||
      commandSurface === Constants.InputMobileOnlineMeetingCommandSurface ||
      commandSurface === Constants.InputMobileLogEventAppointmentAttendee
    );
  }

  private static _isReportPhishingCommandSurface(commandSurface: string): boolean {
    return commandSurface === Constants.InputReportPhishingCommandSurface;
  }

  private static _getRibbonContextFromCommandSurface(commandSurface: string): MOS.Contexts {
    switch (commandSurface) {
      case Constants.InputPrimaryCommandSurface:
        return MOS.Contexts.Default;
      case Constants.InputMessageComposeCommandSurface:
        return MOS.Contexts.MailCompose;
      case Constants.InputMessageReadCommandSurface:
        return MOS.Contexts.MailRead;
      case Constants.InputAppointmentOrganizerCommandSurface:
        return MOS.Contexts.MeetingDetailsOrganizer;
      case Constants.InputAppointmentAttendeeCommandSurface:
        return MOS.Contexts.MeetingDetailsAttendee;
      case Constants.InputMobileLogEventAppointmentAttendee:
        return MOS.Contexts.LogEventMeetingDetailsAttendee;
      case Constants.InputMobileMessageReadCommandSurface:
      case Constants.InputMobileMessageComposeCommandSurface:
        return MOS.Contexts.Default;
      case Constants.InputMobileAppointmentAttendeeCommandSurface:
        return MOS.Contexts.MeetingDetailsAttendee;
      case Constants.InputMobileAppointmentOrganizerCommandSurface:
        return MOS.Contexts.MeetingDetailsOrganizer;
      case Constants.InputMobileOnlineMeetingCommandSurface:
        return MOS.Contexts.OnlineMeetingDetailsOrganizer;
      case Constants.InputReportPhishingCommandSurface:
        return MOS.Contexts.SpamReportingOverride;
      default:
        Utilities.logError("Unsupported command surface: " + commandSurface);
        return MOS.Contexts.Default;
    }
  }

  private static _getFunctionFileResid(
    versionOverrideHostIndex: string,
    versionOverrideIndex: string,
    xmlManifest: OSF.Manifest.Manifest,
  ): Object {
    let functionFileResid: string = null;

    if (!Utilities.isNullOrUndefined(xmlManifest._versionOverrides[versionOverrideHostIndex])) {
      if (
        Utilities.isNullOrUndefined(versionOverrideIndex) &&
        !Utilities.isNullOrUndefined(
          xmlManifest._versionOverrides[versionOverrideHostIndex][Constants.InputFunctionFileResid],
        )
      ) {
        functionFileResid = xmlManifest._versionOverrides[versionOverrideHostIndex][Constants.InputFunctionFileResid];
      } else if (
        !Utilities.isNullOrUndefined(versionOverrideIndex) &&
        !Utilities.isNullOrUndefined(xmlManifest._versionOverrides[versionOverrideHostIndex][versionOverrideIndex]) &&
        !Utilities.isNullOrUndefined(
          xmlManifest._versionOverrides[versionOverrideHostIndex][versionOverrideIndex][
            Constants.InputFunctionFileResid
          ],
        )
      ) {
        functionFileResid =
          xmlManifest._versionOverrides[versionOverrideHostIndex][versionOverrideIndex][
            Constants.InputFunctionFileResid
          ];
      }
    }

    return functionFileResid;
  }

  private static _getAddinCommandsExtensionPoints(
    versionOverrideHostIndex: string,
    versionOverrideIndex: string,
    formFactor: string,
    xmlManifest: OSF.Manifest.Manifest,
  ): [] {
    let addinCommandsExtensionPoints: [] = null;

    if (!Utilities.isNullOrUndefined(xmlManifest._versionOverrides[versionOverrideHostIndex])) {
      // If versionOverrideIndex is null then we are just looking in the main section:  ["MailHost"]
      // If it is not null then we are looking under a specific override under the main host index:  ["MailHost"]["100"] for version override 1.0 etc.
      if (
        Utilities.isNullOrUndefined(versionOverrideIndex) &&
        !Utilities.isNullOrUndefined(
          xmlManifest._versionOverrides[versionOverrideHostIndex][Constants.InputAddinCommandsExtensionPoints],
        )
      ) {
        addinCommandsExtensionPoints =
          xmlManifest._versionOverrides[versionOverrideHostIndex][Constants.InputAddinCommandsExtensionPoints][
            formFactor
          ];
      } else if (
        !Utilities.isNullOrUndefined(versionOverrideIndex) &&
        !Utilities.isNullOrUndefined(xmlManifest._versionOverrides[versionOverrideHostIndex][versionOverrideIndex]) &&
        !Utilities.isNullOrUndefined(
          xmlManifest._versionOverrides[versionOverrideHostIndex][versionOverrideIndex][
            Constants.InputAddinCommandsExtensionPoints
          ] &&
            !Utilities.isNullOrUndefined(
              xmlManifest._versionOverrides[versionOverrideHostIndex][versionOverrideIndex][
                Constants.InputAddinCommandsExtensionPoints
              ][formFactor],
            ),
        )
      ) {
        // Outlook/Mailbox manifest is stored under "100" and "101" for version overrides
        addinCommandsExtensionPoints =
          xmlManifest._versionOverrides[versionOverrideHostIndex][versionOverrideIndex][
            Constants.InputAddinCommandsExtensionPoints
          ][formFactor];
      }
    }

    return addinCommandsExtensionPoints;
  }

  private static _cloneRequirements(requirements: MOS.RequirementsExtensionElement): MOS.RequirementsExtensionElement {
    if (Utilities.isNullOrUndefined(requirements)) {
      return null;
    }

    let req: MOS.RequirementsExtensionElement = new MOS.RequirementsExtensionElement();

    if (Utilities.isNullOrUndefined(requirements.capabilities)) {
      req.capabilities = undefined;
    } else {
      req.capabilities = Converter._cloneCapabilities(requirements.capabilities);
    }

    req.formFactors = !Utilities.isNullOrUndefined(requirements.formFactors)
      ? requirements.formFactors.map((x) => x)
      : undefined;
    //req.formFactors = !Utilities.isNullOrUndefined(requirements.formFactors)
    //  ? requirements.formFactors.map((x) => x)
    //  : undefined;
    req.scopes = !Utilities.isNullOrUndefined(requirements.scopes) ? requirements.scopes.map((x) => x) : undefined;

    return req;
  }

  private static _cloneCapabilities(capabilities: MOS.Capabilities[]): MOS.Capabilities[] {
    if (Utilities.isNullOrUndefined(capabilities)) {
      return undefined;
    }

    let caps: MOS.Capabilities[] = [];

    for (let i = 0; i < capabilities.length; i++) {
      let capability: MOS.Capabilities = capabilities[i];
      let cap: MOS.Capabilities = new MOS.Capabilities();
      cap.name = capability.name;
      cap.minVersion = capability.minVersion;
      cap.maxVersion = capability.maxVersion;

      caps.push(cap);
    }

    return caps;
  }

  private _addRuntimeForFormFactor(context: ConversionContext, runtimeRecords: RuntimeRecord[], formFactor: string, sourceLocation: string): void {
    let runtimeRecord: RuntimeRecord = this._createRuntimeRecord(
      context,
      sourceLocation,
      new MOS.RequirementsExtensionElement(),
      RuntimeSource.DefaultSettingSourceLocations,
      null,
    ); // Capability/ManifestHostType comes from requirements and there can be multiple capabilities

    // Scopes at the base level will part of the requirements at extension level. We don't need to add the Hosts at the runtime level again.
    // this._addScopesFromHosts(<MOS.RequirementsExtensionElement><unknown>runtimeRecord._requirements);

    // now go through and add the default source locations for the different languages
    for (let seenLocale in this._xmlManifest.getLocalesSeen()) {
      // Get the default settings source location for a particular language and add it to the runtime record
      let localeSourceLocation: string = this._xmlManifest.getSourceLocation(seenLocale, formFactor);

      if (!Utilities.isNullOrUndefined(localeSourceLocation)) {
        runtimeRecord._localeResources[seenLocale] = localeSourceLocation;
      }
    }

    this._addRuntimeRecord(runtimeRecord, runtimeRecords);
  }

  private _collectRuntimeRecordsFromRuntimes(
    context: ConversionContext,
    runtimeRecords: RuntimeRecord[],
    runtimes: OSF.Manifest.Runtimes,
    manifestHostType: OSF.ManifestHostType,
    source: RuntimeSource,
  ): void {
    let requirements: MOS.RequirementsExtensionElement = new MOS.RequirementsExtensionElement();
    requirements.capabilities = context.getCurrentVersionOverrideCapabilities();

    for (let idx = 0; idx < runtimes.getRuntimes().length; idx++) {
      let runtime = runtimes.getRuntimes()[idx];

      let scriptResId: string;
      let lifetime: MOS.RuntimesLifetime = MOS.RuntimesLifetime.Short;

      if (!Utilities.isNullOrUndefined(runtime.Overrides)) {
        for (let override of runtime.Overrides) {
          // get the code.script resId
          if (override.Type === "javascript") {
            scriptResId = override.Resid;
            break;
          }
        }
      }

      if (runtime.LifeTime === InputRuntimeLifeTime.Long) {
        lifetime = MOS.RuntimesLifetime.Long;
      }

      let runtimeRecord: RuntimeRecord = this._createRuntimeRecord(
        context,
        runtime.Resid,
        requirements,
        source,
        manifestHostType,
        scriptResId,
        lifetime,
      );

      this._addRuntimeRecord(runtimeRecord, runtimeRecords);
    }
  }

  private _collectRuntimeRecordsFromActions(
    context: ConversionContext,
    runtimeRecords: RuntimeRecord[],
    controls: any,
    manifestHostType: OSF.ManifestHostType,
  ): void {
    if (Utilities.isNullOrUndefined(controls?.length)) return;

    let requirements: MOS.RequirementsExtensionElement = new MOS.RequirementsExtensionElement();
    // TODO: We should set FormFactor
    requirements.capabilities = context.getCurrentVersionOverrideCapabilities();

    for (let idx = 0; idx < controls.length; idx++) {
      let control: any = controls[idx];

      // We only need to create a runtime record for ShowTaskPane because ExecuteFunction runtime records will
      // be added from the encompassing <FunctionFile> tag.
      if (control.actionType !== Constants.InputShowTaskpane && control.controlType !== Constants.InputMenu) {
        continue;
      }

      switch (control.controlType) {
        case Constants.InputButton: // ribbon's top level control is button
        case Constants.InputMenuItem: // context menu's top level control is menu item
          let runtimeRecord: RuntimeRecord = this._createRuntimeRecord(
            context,
            control.action.sourceLocationResid,
            requirements,
            RuntimeSource.ShowTaskpane,
            manifestHostType,
          );

          this._addRuntimeRecord(runtimeRecord, runtimeRecords);

          break;

        case Constants.InputMenu: // for ribbon's menu control
          for (let menuIdx: number = 0; menuIdx < control.children.length; menuIdx++) {
            let menuControl: any = control.children[menuIdx];
            if (menuControl.actionType === Constants.InputShowTaskpane) {
              let runtimeRecord: RuntimeRecord = this._createRuntimeRecord(
                context,
                menuControl.action.sourceLocationResid,
                requirements,
                RuntimeSource.ShowTaskpane,
                manifestHostType,
              );

              this._addRuntimeRecord(runtimeRecord, runtimeRecords);
            }
          }

          break;
      }
    }
  }

  private _createRuntimeRecord(
    context: ConversionContext,
    resource: string,
    requirements: MOS.RequirementsExtensionElement,
    source: RuntimeSource,
    manifestHostType: OSF.ManifestHostType,
    scriptResId: string = null,
    lifetime: MOS.RuntimesLifetime = MOS.RuntimesLifetime.Short
  ): RuntimeRecord {
    let runtimeRecord: RuntimeRecord = new RuntimeRecord();
    runtimeRecord._resource = resource;
    runtimeRecord._requirements = requirements;
    runtimeRecord._source = source;
    runtimeRecord._manifestHostType = manifestHostType;
    runtimeRecord._localeResources = {};
    runtimeRecord._lifetime = lifetime;

    if (!Utilities.isNullOrUndefined(scriptResId)) {
      runtimeRecord._scriptResId = scriptResId;
    }

    if (!Utilities.isNullOrUndefined(context)) {
      runtimeRecord._inputFormFactor = context.getFormFactor();
      runtimeRecord._versionOverridesNumber = context.getCurrentVersionOverrideNumber();
    } else {
      Utilities.logError("Expected the form factor to be set in the context for runtime: " + resource);
    }
    // Record the capabilities (from <Requirements><Sets>) that come from the containing version override.
    // Later when the runtimes are coallesced, these requirements will be merged into requirements.capabilities
    // of the resulting runtimes in the metaos manifest.
    if (
      !Utilities.isNullOrUndefined(context) &&
      !Utilities.isNullOrUndefined(context.getCurrentVersionOverrideCapabilities())
    ) {
      runtimeRecord._versionOverrideCapabilities = context.getCurrentVersionOverrideCapabilities();
    }

    return runtimeRecord;
  }

  private _addRuntimeRecord(runtimeRecord: RuntimeRecord, runtimeRecords: RuntimeRecord[]): void {
    Utilities.log("Adding runtime record: " + runtimeRecord._resource);
    runtimeRecords.push(runtimeRecord);
  }

  private _populateRootRequirements(): void {
    // TODO: Not present in legacy manifest
  }

  private _populateLocaleSensitiveProperty(propertyGetter: Function, mosPropertyName: string, maxLength: number): void {
    const defaultLocale: string = this._xmlManifest.getDefaultLocale();

    for (let seenLocale in this._xmlManifest.getLocalesSeen()) {
      // The default language is put into the main manifest file and not in auxillary localized files
      if (seenLocale != defaultLocale) {
        Utilities.log("handling %s for locale: %s", mosPropertyName, seenLocale);
        let localizedPropertyValue: string = propertyGetter.apply(this._xmlManifest, [seenLocale]);

        if (!Utilities.isNullOrUndefined(localizedPropertyValue)) {
          let finalValue: string = localizedPropertyValue;

          if (!Utilities.isNullOrUndefined(maxLength)) {
            finalValue = Utilities.getLimitedString(
              localizedPropertyValue,
              maxLength,
              mosPropertyName + " truncated to %d characters in localized file " + seenLocale,
            );
          }

          this._localeBuilder.addLocaleString(seenLocale, mosPropertyName, finalValue);
        }
      }
    }
  }

  private _populateValidDomains(): void {
    // Sometimes the underlying parser has a null in the collection but this breaks the metaos
    // schema if we have null in the validDomains so we need to ignore nulls
    const appDomains: string[] = this._xmlManifest.getAppDomains();

    // TODO: Review should empty collection be there regardless if we find any domains?
    this._mosManifest.validDomains = [];

    for (let domain of appDomains) {
      if (!Utilities.isNullOrUndefined(domain)) {
        this._mosManifest.validDomains.push(domain);
      }
    }
  }

  // Population methods below are data that is not present in the Office legacy manifests
  private _populateStaticTabs(): void {}

  private _populateComposeExtensions(): void {}

  private _populateGraphConnector(): void {}

  private _populateConfigurableTabs(): void {}

  private _populateBots(): void {}

  private _populateConnectors(): void {}

  private _populateActivities(): void {}

  private _populateMeetingExtensionDefinition(): void {}

  private _populateSubscriptionOffer(): void {}

  private _populateShowLoadingIndicator(): void {}

  private _populateIsFullScreen(): void {}

  private _populateConfigurableProperties(): void {}

  private _populateDefaultBlockUntilAdminAction(): void {}

  private _populatePublisherDocsUrl(): void {}

  private _populateDefaultInstallScope(): void {}

  private _populateDefaultGroupCapability(): void {}

  getOfficeAppManifest(): OSF.Manifest.OfficeAppManifest {
    return this._xmlManifest;
  }

  getJSON(): string {
    return this._mosManifest.toJSON();
  }

  private _getMailHostOverrides(): string[] {
    let overrides: string[] = [];

    // If there are version overrides for mailhost then we add them
    for (let overrideVersion in this._xmlManifest._versionOverrides[Constants.InputMailHost]) {
      // We get these in reverse order so that we end up with the highest number override first because the higher the number the
      // higher the precedence so the resulting elements in the json manifest should be in that order so that the parser can give precedence to the first one.
      // Also see: _getMailHostVersionOVerrides() that builds the same array but from the enum directly instead of from the OSFManifest parser's version overrides.
      overrides.unshift(overrideVersion);
    }

    return overrides;
  }

  private _doesHostHaveContentInManifest(manifestHostType: OSF.ManifestHostType): boolean {
    let hostType: string = Converter._getVersionOverrideHostTypeFromOsfManifestHostType(manifestHostType);
    return !Utilities.isNullOrUndefined(this._xmlManifest._versionOverrides[hostType]);
  }

  private _getResourceAndWriteLocalizedValues(
    resId: string,
    defaultValue: string,
    maxLength: number,
    versionOverridesNumber: OSF.VersionOverridesNumber,
    resourceTemplate: string,
    resourceType: ResourceType,
    index1: number = undefined,
    index2: number = undefined,
    index3: number = undefined,
    index4: number = undefined,
    index5: number = undefined,
    index6: number = undefined,
  ): string {
    let resources: object = this._xmlManifest.getResources(versionOverridesNumber);

    if (Utilities.isNullOrUndefined(resources)) {
      return !Utilities.isNullOrUndefined(defaultValue) ? defaultValue : resId;
    }

    let allLocaleResources: object = resources[resourceType];

    if (Utilities.isNullOrUndefined(allLocaleResources)) {
      return !Utilities.isNullOrUndefined(defaultValue) ? defaultValue : resId;
    }

    const defaultLocale: string = this._xmlManifest.getDefaultLocale().toLocaleLowerCase();

    if (
      Utilities.isNullOrUndefined(allLocaleResources[resId]) ||
      Utilities.isNullOrUndefined(allLocaleResources[resId][defaultLocale])
    ) {
      // return back the resId if we can't find a matching resource
      return !Utilities.isNullOrUndefined(defaultValue) ? defaultValue : resId;
    }

    // Add localized runtime strings for other languages besides the default language
    for (let seenLocale in this._xmlManifest.getLocalesSeen()) {
      if (seenLocale !== defaultLocale && !Utilities.isNullOrUndefined(allLocaleResources[resId][seenLocale])) {
        let formattedValue: string = Converter._formatResourceStringWithVariableNumberParameters(
          resourceTemplate,
          index1,
          index2,
          index3,
          index4,
          index5,
          index6,
        );

        // For images, we also need to add them to the iconBuilder and store the package file reference in the localization file
        if (resourceType === ResourceType.AllLocaleImages) {
          const packageReference: string = this._iconBuilder.addIconAndGetReference(
            allLocaleResources[resId][seenLocale],
            !Utilities._writeImageUrls,
            false,
          );
          this._localeBuilder.addLocaleString(seenLocale, formattedValue, packageReference);
        } else {
          this._localeBuilder.addLocaleString(seenLocale, formattedValue, allLocaleResources[resId][seenLocale]);
        }
      }
    }

    // Return the string for the default language that will be written into the main manifest file
    const valueObtained: string = Utilities.isNullOrUndefined(defaultValue)
      ? allLocaleResources[resId][defaultLocale]
      : defaultValue;
    return maxLength == Constants.MaxResourceStringLengthAny
      ? valueObtained
      : Utilities.truncateString(valueObtained, maxLength);
  }

  private _addConstantResourceForAllSeenLocales(
    value: string,
    resourceTemplate: string,
    index1: number,
    index2: number,
    index3: number,
    index4: number,
    index5: number,
    index6: number,
  ): void {
    const defaultLocale: string = this._xmlManifest.getDefaultLocale().toLocaleLowerCase();

    for (let seenLocale in this._xmlManifest.getLocalesSeen()) {
      if (seenLocale !== defaultLocale) {
        let formattedValue: string = Converter._formatResourceStringWithVariableNumberParameters(
          resourceTemplate,
          index1,
          index2,
          index3,
          index4,
          index5,
          index6,
        );
        this._localeBuilder.addLocaleString(seenLocale, formattedValue, value);
      }
    }
  }

  private static _incrementCurrentRibbonIndex(context: ConversionContext): void {
    // Keep track of a count of the ribbon index for purposes of the localization indexed strings
    if (context.getCurrentRibbonIndex() === ConversionContext.UnsetIndex) {
      context.setCurrentRibbonIndex(0);
    } else {
      context.setCurrentRibbonIndex(context.getCurrentRibbonIndex() + 1);
    }
  }

  private static _formatResourceStringWithVariableNumberParameters(
    resourceTemplate: string,
    index1: number = undefined,
    index2: number = undefined,
    index3: number = undefined,
    index4: number = undefined,
    index5: number = undefined,
    index6: number = undefined,
  ): string {
    if (Utilities.isNullOrUndefined(index1) || index1 === ConversionContext.UnsetIndex) {
      return resourceTemplate;
    } else if (Utilities.isNullOrUndefined(index2) || index2 === ConversionContext.UnsetIndex) {
      return util.format(resourceTemplate, index1);
    } else if (Utilities.isNullOrUndefined(index3) || index3 === ConversionContext.UnsetIndex) {
      return util.format(resourceTemplate, index1, index2);
    } else if (Utilities.isNullOrUndefined(index4) || index4 === ConversionContext.UnsetIndex) {
      return util.format(resourceTemplate, index1, index2, index3);
    } else if (Utilities.isNullOrUndefined(index5) || index5 === ConversionContext.UnsetIndex) {
      return util.format(resourceTemplate, index1, index2, index3, index4);
    } else if (Utilities.isNullOrUndefined(index6) || index6 === ConversionContext.UnsetIndex) {
      return util.format(resourceTemplate, index1, index2, index3, index4, index5);
    } else {
      return util.format(resourceTemplate, index1, index2, index3, index4, index5, index6);
    }
  }

  private _addRuntimeExecuteAction(
    runtime: MOS.Runtimes,
    action: MOS.ExtensionRuntimesActionsItem,
    runtimeKey: string,
  ): void {
    Utilities.assert(
      action.type == MOS.ExtensionRuntimesActionsItemType.ExecuteFunction,
      "Expected an executeFunction type of action.",
    );

    const runtimeActionKey: string = Converter._generateExecuteActionKey(runtimeKey, action.id);

    // If an ExecuteFunction action with this same actionId already is in this runtime then we don't add it
    if (
      Utilities.isNullOrUndefined(this._addedExecuteFunctions[runtimeActionKey]) ||
      !this._addedExecuteFunctions[runtimeActionKey]
    ) {
      runtime.actions.push(action);
      this._addedExecuteFunctions[runtimeActionKey] = true;
      Utilities.log("Added action %s for key: %s", action.id, runtimeActionKey);
    } else {
      Utilities.log(
        "Skipped adding action %s for key: %s because an identical one already exists.",
        action.id,
        runtimeActionKey,
      );
    }
  }

  private static _getVersionOverrideHostTypeFromOsfManifestHostType(manifestHostType: OSF.ManifestHostType): string {
    switch (manifestHostType) {
      case OSF.ManifestHostType.Document:
        return Constants.InputDocument;
      case OSF.ManifestHostType.Workbook:
        return Constants.InputWorkbook;
      case OSF.ManifestHostType.Presentation:
        return Constants.InputPresentation;
      case OSF.ManifestHostType.Mailbox:
        return Constants.InputMailHost;
      default:
        throw "unsupported manifest host type: " + manifestHostType;
    }
  }

  private static _getCapabilityVersionFromNumericVersion(overrideText: string): string {
    switch (overrideText) {
      case OSF.VersionOverridesNumber.V1_0.toString():
        return Constants.MosV100;
      case OSF.VersionOverridesNumber.V1_1.toString():
        return Constants.MosV101;
      default:
        Utilities.logError("Unrecognized override version: " + overrideText);
        return "";
    }
  }

  private static _getNumericOverrideVersionFromString(overrideText: string): OSF.VersionOverridesNumber {
    switch (overrideText) {
      case OSF.VersionOverridesNumber.V1_0.toString():
        return OSF.VersionOverridesNumber.V1_0;
      case OSF.VersionOverridesNumber.V1_1.toString():
        return OSF.VersionOverridesNumber.V1_1;
      default:
        Utilities.logError("Unrecognized override version: " + overrideText);
        return null;
    }
  }

  private static _getRequirementsScopeFromManifestHostType(manifestHostType: string): MOS.Scopes {
    switch (manifestHostType) {
      case OSF.ManifestHostType.Mailbox:
        return MOS.Scopes.Mail;
      case OSF.ManifestHostType.Workbook:
        return MOS.Scopes.Workbook;
      case OSF.ManifestHostType.Document:
        return MOS.Scopes.Document;
      case OSF.ManifestHostType.Presentation:
        return MOS.Scopes.Presentation;
      default:
        Utilities.logError("Unsupported manifestHostType " + manifestHostType);
    }
  }

  private static _mosSendModeFromXmlSendMode(sendMode: string): MOS.OptionsSendMode {
    switch (sendMode.toLowerCase()) {
      case Constants.InputSendModeBlock:
        return MOS.OptionsSendMode.Block;
      case Constants.InputSendModePromptUser:
        return MOS.OptionsSendMode.PromptUser;
      case Constants.InputSendModeSoftBlock:
        return MOS.OptionsSendMode.SoftBlock;
      default:
        Utilities.logError("Unsupported sendMode: '%s'. Setting to null.", sendMode);
        return null;
    }
  }

  private static _mosPermissionfromXmlPermission(permission: number, appType: OSF.OfficeAppType): string {
    if (appType === OSF.OfficeAppType.ContentApp || appType === OSF.OfficeAppType.TaskPaneApp) {
      // Content App Permissions: [Restricted | ReadDocument | ReadAllDocument | WriteDocument | ReadWriteDocument]
      switch (permission) {
        case OSF.OsfControlPermission.Restricted:
          return Constants.DocumentRestrictedUser;
        case OSF.OsfControlPermission.ReadDocument:
          return Constants.DocumentReadUser;
        case OSF.OsfControlPermission.WriteDocument:
          return Constants.DocumentWriteUser;
        case OSF.OsfControlPermission.ReadAllDocument:
          return Constants.DocumentReadAllUser; // TODO: Need to get the correct name in metaos WI:6247317
        case OSF.OsfControlPermission.ReadWriteDocument:
          return Constants.DocumentReadWriteUser; // TODO:Need to get the correct name in metaos WI:6247317
        default:
          Utilities.logError("Unknown <Permission> found for ContentApp/TaskpaneApp: " + permission);
      }
    } else if (appType === OSF.OfficeAppType.MailApp) {
      // MailApp Permissions: [Restricted | ReadItem | ReadWriteItem | ReadWriteMailbox]
      switch (permission) {
        case OSF.OsfControlPermission.Restricted:
          return Constants.MailboxItemRestrictedUser;
        case OSF.OsfControlPermission.ReadItem:
          return Constants.MailboxItemReadUser;
        case OSF.OsfControlPermission.ReadWriteItem:
          return Constants.MailboxItemReadWriteUser;
        case OSF.OsfControlPermission.ReadWriteMailbox:
          return Constants.MailboxReadWriteUser;
        default:
          Utilities.logError("Unknown <Permission> found for MailApp: " + permission);
      }
    }
  }

  private static _setVersionOverrideNumberAndCapabilitiesInContext(
    context: ConversionContext,
    xmlManifest: any,
    hostType: string,
    versionOverridesIndex: string,
  ): void {
    let capabilities: MOS.Capabilities[] = null;
    let versionOverride: Object = null;
    let versionOverrideIndexNumeric: OSF.VersionOverridesNumber = null;

    if (Converter._isWXP(hostType)) {
      versionOverride = xmlManifest._versionOverrides[hostType];
    } else {
      versionOverride = xmlManifest._versionOverrides[hostType][versionOverridesIndex];
      versionOverrideIndexNumeric = Converter._getNumericOverrideVersionFromString(versionOverridesIndex);
    }

    if (
      !Utilities.isNullOrUndefined(versionOverride) &&
      !Utilities.isNullOrUndefined(versionOverride[Constants.InputRequirements]) &&
      !Utilities.isNullOrUndefined(versionOverride[Constants.InputRequirements][Constants.InputSets])
    ) {
      capabilities = Converter._getCapabilitiesFromLegacyRequirementSets(
        versionOverride[Constants.InputRequirements][Constants.InputSets],
      );
    }

    // Set implicit capabilty for the versionOverride
    let capability: MOS.Capabilities = this._getImplicitCapabilityBasedOnVersionOverrideForHost(
      hostType,
      versionOverridesIndex,
    );
    if (!Utilities.isNullOrUndefined(capability)) {
      if (Utilities.isNullOrUndefined(capabilities)) {
        capabilities = [];
      }
      capabilities.push(capability);
    }

    // Merge the added capabilities
    let requirements: MOS.RequirementsExtensionElement = new MOS.RequirementsExtensionElement();
    Converter._mergeInVersionOverrideRequirements(capabilities, requirements);

    // If there is an override present in the manifest then we set the capabilities and this override in the context
    // so that subsequent population will understand and be able to set requirements correctly.
    if (!Utilities.isNullOrUndefined(versionOverride)) {
      context.setCurrentVersionOverrideAndCapabilities(versionOverrideIndexNumeric, requirements.capabilities);
    }
  }

  private static _clearVersionOverrideNumberAndCapabilitiesInContext(context: ConversionContext): void {
    context.setCurrentVersionOverrideAndCapabilities(null, null);
  }

  private static _legacyLaunchEventToMosEventType(legacyEventType: string): string {
    switch (legacyEventType) {
      case "OnNewMessageCompose":
        return "newMessageComposeCreated";
      case "OnNewAppointmentOrganizer":
        return "newAppointmentOrganizerCreated";
      case "OnMessageAttachmentsChanged":
        return "messageAttachmentsChanged";
      case "OnAppointmentAttachmentsChanged":
        return "appointmentAttachmentsChanged";
      case "OnMessageRecipientsChanged":
        return "messageRecipientsChanged";
      case "OnAppointmentAttendeesChanged":
        return "appointmentAttendeesChanged";
      case "OnAppointmentTimeChanged":
        return "appointmentTimeChanged";
      case "OnAppointmentRecurrenceChanged":
        return "appointmentRecurrenceChanged";
      case "OnInfoBarDismissClicked":
        return "infoBarDismissClicked";
      case "OnMessageSend":
        return "messageSending";
      case "OnAppointmentSend":
        return "appointmentSending";
      case "OnOpenDocument":
        return "documentOpen";
      case "OnMessageCompose":
        return "messageComposeOpened";
      case "OnAppointmentOrganizer":
        return "appointmentOrganizerOpened";
      default:
        Utilities.logError("Unrecognized autorun even type: " + legacyEventType);
    }

    return legacyEventType;
  }

  static _generateExecuteActionKey(runtimeKey: string, actionId: string): string {
    return runtimeKey + actionId;
  }

  static _generateRuntimeLookupKey(
    url: string,
    lifetime: MOS.RuntimesLifetime,
    capabilities: MOS.Capabilities[],
    formFactor: string = null,
    source: RuntimeSource = null
  ): string {
    // Currently we are building the key for the Runtime using the URL, lifetime and Capabilities
    // URL: URL is the primary thing which is required for the Runtime this has to be unqiue
    // Lifetime: As we can have the same URL with Lifetime Long(SharedRuntime) and Short
    // Capabilities: A URL can be found inside a VersionOverride 1_0 (Like Capability MailBox 1.3) and the same URL can be found for VersionOverrde 1_1(Mailbox 1.5)

    let resultKey: string = url + lifetime.toString();

    if (source === RuntimeSource.DefaultSettingSourceLocations) {
      resultKey += "_ContentRuntime_";
    }

    if (!Utilities.isNullOrUndefined(formFactor)) {
      resultKey = resultKey + formFactor;
    }

    if (!Utilities.isNullOrUndefined(capabilities)) {
      Converter._sortCapabilitiesAlphabatically(capabilities);
      for (let capability of capabilities) {
        resultKey = resultKey + (capability.name + capability.minVersion);
      }
    }

    return resultKey;
  }

  static _sortCapabilitiesAlphabatically(capabilities: MOS.Capabilities[]): void {
    if (!Utilities.isNullOrUndefined(capabilities)) {
      capabilities.sort(function (c1, c2) {
        if (c1.name < c2.name) {
          return -1;
        }

        if (c1.name > c2.name) {
          return 1;
        }

        return 0;
      });
    }
  }

  static _getImplicitCapabilityBasedOnVersionOverrideForHost(
    hostType: string,
    versionOverridesIndex: string,
  ): MOS.Capabilities {
    let capability: MOS.Capabilities = null;
    // The requirementsets are taken from the public document specified here: https://docs.microsoft.com/en-us/javascript/api/manifest/versionoverrides?view=excel-js-preview#variant-schemas
    if (hostType === Constants.InputMailHost) {
      if (versionOverridesIndex === OSF.VersionOverridesNumber.V1_0.toString()) {
        capability = new MOS.Capabilities();
        capability.name = Constants.InputMailBoxCapability;
        capability.minVersion = "1.3";
      } else if (versionOverridesIndex === OSF.VersionOverridesNumber.V1_1.toString()) {
        capability = new MOS.Capabilities();
        capability.name = Constants.InputMailBoxCapability;
        capability.minVersion = "1.5";
      }
    } else if (Converter._isWXP(hostType)) {
      if (versionOverridesIndex === OSF.VersionOverridesNumber.V1_0.toString()) {
        capability = new MOS.Capabilities();
        capability.name = Constants.InputAddinCommandCapability;
        capability.minVersion = "1.1";
      }
    } else {
      Utilities.logError(
        "Skipping adding VersionOverride capability for the host as unsupported host type: %s",
        hostType,
      );
    }

    return capability;
  }

  static _isWXP(hostType: string): boolean {
    return (
      hostType === Constants.InputDocument ||
      hostType === Constants.InputPresentation ||
      hostType === Constants.InputWorkbook
    );
  }

  static _getMailHostVersionOVerrides(): string[] {
    let versionOverrides = [];

    const values = Object.values(OSF.VersionOverridesNumber);
    // We should fill the array in reverse array as we need to iterate from maximum version to minimum in order to maintain the order
    values
      .slice()
      .reverse()
      .forEach((value) => {
        if (!isNaN(Number(value)) && Number(value) !== OSF.VersionOverridesNumber.Max) {
          versionOverrides.push(value.toString());
        }
      });

    return versionOverrides;
  }

  static _oSFFormFactorToInputFormFactor(osfFormFactor: string): string {

    switch(osfFormFactor) {
      case OSF.FormFactor.Default:
      case OSF.FormFactor.Desktop:
        return Constants.InputDesktopFormFactor;
        break;
      case OSF.FormFactor.Phone:
        return Constants.InputMobileFormFactor;
        break;
      case OSF.FormFactor.Tablet:
        return Constants.InputTabletFormFactor;
        break;
      default:
        return Constants.InputDesktopFormFactor;
        break;
    }
  }

  static _inputFormFactorToMOSFormFactor(inputFormFactor: string): MOS.FormFactors {
    if (inputFormFactor === Constants.InputDesktopFormFactor) {
      return MOS.FormFactors.Desktop;
    } else if (inputFormFactor === Constants.InputMobileFormFactor) {
      return MOS.FormFactors.Mobile;
    } else {
      Utilities.logError("Unrecognized or unused form factor: " + inputFormFactor);
      return MOS.FormFactors.Desktop;
    }
  }

  static _fixVersionFormat(versionString: string) {
  
    // Return this as is if it can't be parsed as a version string
    if (Utilities.isNullOrUndefined(versionString)) {
      return versionString;
    }

    // If the version is a four part format like this "1.0.1.4", then we cut the last
    // segment off to match the mos manifest version format http://semver.org which is
    // three segments:  "1.0.1" etc.
    var parts:string[] = versionString.split(".");
    if (parts.length == 4) {
      const newVersionString: string = parts[0] + "." + parts[1] + "." + parts[2];
      Utilities.log("Rewriting version from %s to %s.", versionString, newVersionString);
      return newVersionString;
    }

    // If the incoming version string is not in the four part dot format then we just
    // return it as is and the developer will need to hand edit it according to what they want.
    Utilities.logError("Leaving version as %s but this will needed to be edited to conform to the format described in http://semver.org", versionString);
    return versionString;
  }
}